import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Row, Col, Dropdown } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Navbar, CustomButton, LoadingSpinner, CustomModal } from '../components';
import { avatarsAPI, authAPI, plansAPI, projectsAPI, paymentsAPI } from '../services/api';
import { hasActiveSubscription, fetchActiveSubscriptionFromApi, isActiveBetaUser, shouldRouteInviteeToChat } from '../utils/freeTrial';
import { RELATION_OPTIONS } from '../constants/relations';
import './CreateAvatarPage.css';

// --- Stylized PayPal Logo Component ---
const PayPalLogo = () => (
  <img
    src="https://www.paypalobjects.com/webstatic/en_US/i/buttons/PP_logo_h_100x26.png"
    alt="PayPal"
    style={{ height: '24px', display: 'block' }}
  />
);

// Normalize avatar type name for matching (dynamic from API: "Monthly Plan", "Yearly Plan", etc.)
const normalizeAvatarTypeName = (name) => {
  if (!name || typeof name !== 'string') return '';
  return name.toLowerCase().trim();
};

// Extensions and MIME prefixes for video/image/audio — these are ignored during upload (files + folders)
const IGNORED_MEDIA_EXTENSIONS = [
  '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg', '.ico', '.tiff', '.tif', '.heic', '.heif', '.raw', '.cr2', '.nef', '.orf', '.sr2', '.psd', '.ai', '.eps',
  '.mp4', '.webm', '.mov', '.avi', '.mkv', '.m4v', '.wmv', '.flv', '.mpeg', '.mpg', '.3gp', '.ogv', '.ts', '.mts', '.m2ts',
  '.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac', '.wma', '.aiff', '.aif', '.opus', '.weba', '.amr', '.au', '.ra', '.rm', '.wv', '.ape', '.dsd',
  '.sql'
];
const IGNORED_MEDIA_MIME_PREFIXES = ['image/', 'video/', 'audio/'];
const KEEP_ARCHIVE_EXTENSIONS = ['.zip', '.rar', '.7z', '.tar', '.gz', '.bz2', '.xz'];

const isVideoImageOrAudioFile = (file) => {
  if (!file || !file.name) return false;
  const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
  if (KEEP_ARCHIVE_EXTENSIONS.includes(ext)) return false; // never filter archives
  if (IGNORED_MEDIA_EXTENSIONS.includes(ext)) return true;
  const mime = (file.type || '').toLowerCase();
  return IGNORED_MEDIA_MIME_PREFIXES.some((p) => mime.startsWith(p));
};

// Block .xls, .xlsx, .exe from upload list (folder or drag-and-drop)
const BLOCKED_EXTENSIONS = ['.exe'];
const isBlockedFile = (file) => {
  if (!file || !file.name) return false;
  const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
  return BLOCKED_EXTENSIONS.includes(ext);
};

// Treat API placeholder entries as non-invitees so we show empty state instead of "You" / you@example.com.
const isPlaceholderInviteEntry = (email, name) => {
  const e = (email || '').trim().toLowerCase();
  const n = (name || '').trim().toLowerCase();
  if (e === 'you@example.com' || e === 'example@example.com' || e === 'user@example.com') return true;
  if (e.startsWith('user@') && e.includes('example')) return true;
  if (n === 'you' && (!e || e.includes('example'))) return true;
  if (n === 'user' && (!e || e.includes('example'))) return true;
  return false;
};

// Deduplicate emails (case-insensitive) while keeping stable order.
// If duplicates exist, prefer any truthy `selected` and fill missing `name`.
const dedupeEmailObjects = (emails) => {
  if (!Array.isArray(emails)) return [];
  const seen = new Map(); // key=emailLower -> index in out
  const out = [];
  for (const item of emails) {
    if (!item) continue;
    const raw = (item.email || '').trim();
    if (!raw) continue;
    const key = raw.toLowerCase();
    if (!seen.has(key)) {
      const normalized = { ...item, email: raw };
      seen.set(key, out.length);
      out.push(normalized);
      continue;
    }
    const idx = seen.get(key);
    const existing = out[idx];
    out[idx] = {
      ...existing,
      selected: Boolean(existing.selected) || Boolean(item.selected),
      name: (existing.name && String(existing.name).trim()) ? existing.name : item.name,
    };
  }
  return out;
};

const CREATE_AVATAR_PAYMENT_DRAFT_KEY = 'create_avatar_payment_draft';
const CREATE_AVATAR_PAYMENT_DRAFT_KEY_PREFIX = `${CREATE_AVATAR_PAYMENT_DRAFT_KEY}_`;
const CREATE_AVATAR_AUTH_FINGERPRINT_KEY = 'create_avatar_auth_fingerprint';
const ACTIVATED_PLAN_KEY_PREFIX = 'activated_plan_for_user_';
const GIFT_RETURN_CONTEXT_KEY = 'create_avatar_gift_return_context';
const INVITE_MODAL_GIFT_EMAILS_KEY = 'invite_modal_gift_emails';

const CreateAvatarPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [avatarType, setAvatarType] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [actualFiles, setActualFiles] = useState([]); // Store actual File objects
  const [isDragging, setIsDragging] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('yearly');
  const [autoRenew, setAutoRenew] = useState(true);
  const [userPlanType, setUserPlanType] = useState(null);
  const [avatarPlans, setAvatarPlans] = useState([]); // Avatar types with plans from API
  const [showFileLimitModal, setShowFileLimitModal] = useState(false);
  const [fileLimitFileCount, setFileLimitFileCount] = useState(0);
  const [jobId, setJobId] = useState(null);
  const [progressMessage, setProgressMessage] = useState('');
  const [progressStage, setProgressStage] = useState('reading');
  const [progressWidth, setProgressWidth] = useState(0);
  const progressWidthRef = useRef(0);
  const creepIntervalRef = useRef(null);
  const [loadingPlans, setLoadingPlans] = useState(false); // Loading state for avatar plans
  const [selectedPlanId, setSelectedPlanId] = useState(null); // Selected plan ID for loved one
  const [selectedPlan, setSelectedPlan] = useState(null); // Normalized selected plan for checkout
  const [suggestionFlag, setSuggestionFlag] = useState(false); // Flag to indicate if plan should be auto-selected
  const [paymentActivatedPlan, setPaymentActivatedPlan] = useState(null); // { avatar_plan_id, plan_interval, subscription_id, invoice_id }
  const [userId, setUserId] = useState(null); // Cached user ID to avoid multiple API calls
  const [pendingAvatarData, setPendingAvatarData] = useState(null); // Store avatar data while processing payment

  const uploadedFilesRef = useRef(null);
  const UPLOADED_FILES_COLLAPSED_COUNT = 6;
  const [showInviteModal, setShowInviteModal] = useState(false); // Show invite email modal after avatar creation
  const [inviteAvatarName, setInviteAvatarName] = useState(''); // Avatar first name shown in the invite modal heading
  const [inviteRows, setInviteRows] = useState([]); // Rows shown in invite modal (selected + action)
  const [createdAvatarId, setCreatedAvatarId] = useState(null); // Store created avatar ID for invite API call
  const [sendingInvites, setSendingInvites] = useState(false); // Loading state for sending invites
  const [avatarPlanDetails, setAvatarPlanDetails] = useState(null); // Store avatar plan pricing options
  const [loadingAvatarPlan, setLoadingAvatarPlan] = useState(false); // Loading state for avatar plan
  const [openDropdownId, setOpenDropdownId] = useState(null); // Track which dropdown is open
  const [dropdownOpensUpward, setDropdownOpensUpward] = useState({}); // Track which dropdowns should open upward
  const [dropdownPosition, setDropdownPosition] = useState({}); // { [rowId]: { left, top, width } } for fixed-position dropdown
  const [relations, setRelations] = useState([]); // Relations from API
  const [loadingRelations, setLoadingRelations] = useState(false); // Loading state for relations
  const [selectedRelation, setSelectedRelation] = useState(null); // Selected relation ID
  const authCheckedRef = useRef(false);
  const userFetchedRef = useRef(false);
  const hasRestoredPaymentDraftRef = useRef(false);
  const restoredPlanIdRef = useRef(null);
  const orderCreatingRef = useRef(false);      // Bug 1: prevent duplicate processPayment calls
  const inviteModalPaypalCleanupRef = useRef(null);
  const [userData, setUserData] = useState(null); // Cache full user data
  const isBetaInviter = isActiveBetaUser(userData);

  // Family invitees join the inviter's avatar — skip create-avatar and go straight to chat.
  useEffect(() => {
    if (!isAuthenticated || !userData) return;
    if (shouldRouteInviteeToChat({ userData })) {
      navigate('/chat', { replace: true });
    }
  }, [isAuthenticated, userData, navigate]);

  const [showSignupInviteModal, setShowSignupInviteModal] = useState(false);
  const [inviterName, setInviterName] = useState('');
  const [signupInviteProcessed, setSignupInviteProcessed] = useState(false);
  const [duplicateNameError, setDuplicateNameError] = useState('');
  const getCurrentSessionUserId = () => {
    const fromState = Number(userId);
    if (Number.isFinite(fromState) && fromState > 0) return fromState;
    const fromSession = Number.parseInt(sessionStorage.getItem('user_id') || '', 10);
    return Number.isFinite(fromSession) && fromSession > 0 ? fromSession : null;
  };

  const getPaymentDraftKey = (uid) => `${CREATE_AVATAR_PAYMENT_DRAFT_KEY_PREFIX}${uid}`;
  const getActivatedPlanKey = (uid) => `${ACTIVATED_PLAN_KEY_PREFIX}${uid}`;

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: ''
    }
  });

  // Check authentication on mount and fetch user ID once
  useEffect(() => {
    if (authCheckedRef.current) return;

    const token = localStorage.getItem('access_token');
    const fingerprint = token ? token.slice(-24) : '';
    const cachedFingerprint = sessionStorage.getItem(CREATE_AVATAR_AUTH_FINGERPRINT_KEY) || '';
    if (cachedFingerprint && fingerprint && cachedFingerprint !== fingerprint) {
      sessionStorage.removeItem('user_fetched');
      sessionStorage.removeItem('user_id');
      sessionStorage.removeItem('paypal_capture_result');
      const keysToDelete = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (!key) continue;
        if (
          key.startsWith(CREATE_AVATAR_PAYMENT_DRAFT_KEY_PREFIX) ||
          key.startsWith(ACTIVATED_PLAN_KEY_PREFIX) ||
          key.startsWith('payment_processed_') ||
          key.startsWith('payment_processing_')
        ) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach((key) => sessionStorage.removeItem(key));
    }
    if (fingerprint) {
      sessionStorage.setItem(CREATE_AVATAR_AUTH_FINGERPRINT_KEY, fingerprint);
    }

    let cancelled = false;

    if (!token) {
      authCheckedRef.current = true;
      toast.info('Please sign in to create an avatar');
      navigate('/signin');
    } else {
      // Check for active subscription before allowing access
      const apiBase = (process.env.REACT_APP_API_URL || 'https://sayagainservices.sayagain.ai/api').replace(/\/api$/, '');
      fetch(`${apiBase}/api/addons/my-plan`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(async (res) => {
        if (cancelled) return;
        
        const storedInviteParams = JSON.parse(localStorage.getItem('signup_invite_params') || '{}');
        const hasInvite = storedInviteParams.invite_id || storedInviteParams.avatar_id;

        if (!res.ok && !hasInvite) {
          navigate('/select-plan', { replace: true });
        } else {
          authCheckedRef.current = true;
          setIsAuthenticated(true);
          // Refresh user profile so subscription/trial is available for avatar creation checks.
          if (res.ok) {
            try {
              const userResponse = await authAPI.getCurrentUser();
              if (userResponse?.data) {
                setUserData(userResponse.data);
                sessionStorage.setItem('user_data', JSON.stringify(userResponse.data));
              }
              const uid = userResponse?.data?.id || userResponse?.data?.user_id || sessionStorage.getItem('user_id');
              if (uid) {
                const avatarsResponse = await avatarsAPI.getAvatars(uid);
                const existingAvatars = avatarsResponse?.data?.avatars || avatarsResponse?.data || [];
                if (Array.isArray(existingAvatars) && existingAvatars.length > 0) {
                  try {
                    sessionStorage.setItem('avatars_data', JSON.stringify(existingAvatars));
                    sessionStorage.setItem('has_avatars', 'true');
                  } catch (_) { /* ignore */ }
                  navigate('/chat', { replace: true });
                  return;
                }
              }
            } catch (_) { /* non-critical */ }
          }
          fetchUserOnce(true);
          fetchAvatarPlans();
          fetchRelations();
        }
      }).catch(() => {
        if (cancelled) return;
        navigate('/select-plan', { replace: true });
      });
    }

    return () => { cancelled = true; };
  }, [navigate]);

  useEffect(() => {
    const current = getCurrentSelectedPlan();
    setSelectedPlan(current);
  }, [avatarPlans, avatarType, selectedPlanId, billingPeriod]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('paypal_capture_result');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed?.avatar_plan_id || !parsed?.plan_interval) return;
      setPaymentActivatedPlan(parsed);
      const currentUid = getCurrentSessionUserId();
      if (currentUid) {
        sessionStorage.setItem(
          getActivatedPlanKey(currentUid),
          JSON.stringify({
            avatar_plan_id: parsed.avatar_plan_id,
            plan_interval: parsed.plan_interval,
            auto_renew: Boolean(parsed.auto_renew),
            subscription_id: parsed.subscription_id || null,
            invoice_id: parsed.invoice_id || null,
            invoice_download_url: parsed.invoice_download_url || null,
            order_id: parsed.order_id || null,
          })
        );
      }
    } catch (err) {
      console.warn('Failed to read captured payment context:', err);
    }
  }, []);

  useEffect(() => {
    // Restore paid plan activation for this user (prevents re-payment prompt after file reupload/reload).
    try {
      const currentUid = getCurrentSessionUserId();
      if (!currentUid) return;
      const raw = sessionStorage.getItem(getActivatedPlanKey(currentUid));
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed?.avatar_plan_id || !parsed?.plan_interval) return;
      setPaymentActivatedPlan(parsed);
    } catch (err) {
      console.warn('Failed to restore activated plan from session:', err);
    }
  }, [userId]);

  useEffect(() => {
    // Restore per-user form draft after returning from payment flow.
    try {
      const currentUid = getCurrentSessionUserId();
      if (!currentUid) return;
      const rawDraft = sessionStorage.getItem(getPaymentDraftKey(currentUid));
      if (!rawDraft) return;
      const draft = JSON.parse(rawDraft);
      if (!draft || Number(draft.user_id) !== Number(currentUid)) return;

      if (typeof draft.firstName === 'string') setValue('firstName', draft.firstName);
      if (typeof draft.lastName === 'string') setValue('lastName', draft.lastName);
      if (typeof draft.avatarType === 'string' && draft.avatarType) setAvatarType(draft.avatarType);
      if (typeof draft.selectedRelation === 'string' && draft.selectedRelation) setSelectedRelation(draft.selectedRelation);
      if (draft.selectedPlanId !== undefined && draft.selectedPlanId !== null) setSelectedPlanId(draft.selectedPlanId);
      if (typeof draft.billingPeriod === 'string' && draft.billingPeriod) setBillingPeriod(draft.billingPeriod);
      if (typeof draft.autoRenew === 'boolean') setAutoRenew(draft.autoRenew);

      // Restore file list display only (browser cannot restore true File objects after redirect).
      if (Array.isArray(draft.uploadedFiles) && draft.uploadedFiles.length > 0) {
        const restoredDisplayFiles = draft.uploadedFiles.map((file, idx) => ({
          id: file?.id ?? `restored-${Date.now()}-${idx}`,
          name: file?.name || `Uploaded file ${idx + 1}`,
          size: Number(file?.size || 0),
          type: file?.type || '',
          isRestored: true,
        }));
        setUploadedFiles(restoredDisplayFiles);
      }
      hasRestoredPaymentDraftRef.current = true;
    } catch (err) {
      console.warn('Failed to restore create-avatar payment draft:', err);
    }
  }, [setValue, userId]);

  useEffect(() => {
    // Keep paid plan auto-selected for this logged-in user session.
    if (!paymentActivatedPlan?.avatar_plan_id || !Array.isArray(avatarPlans) || avatarPlans.length === 0) return;

    // Prevent overwriting user selection if we've already restored this plan
    if (restoredPlanIdRef.current === paymentActivatedPlan.avatar_plan_id) return;

    const paidPlanId = Number(paymentActivatedPlan.avatar_plan_id);
    if (!Number.isFinite(paidPlanId)) return;

    let matchedAvatarType = null;
    let matchedPlan = null;
    for (const avatarTypeItem of avatarPlans) {
      const plans = Array.isArray(avatarTypeItem?.plans) ? avatarTypeItem.plans : [];
      const found = plans.find((p) => Number(p?.id) === paidPlanId);
      if (found) {
        matchedAvatarType = avatarTypeItem;
        matchedPlan = found;
        break;
      }
    }
    if (!matchedAvatarType || !matchedPlan) return;

    if (matchedAvatarType?.name) setAvatarType(String(matchedAvatarType.name).toLowerCase());
    setSelectedPlanId(Number(matchedPlan.id));
    const paidInterval = String(paymentActivatedPlan.plan_interval || '').toLowerCase();
    if (paidInterval === 'monthly' || paidInterval === 'yearly') {
      setBillingPeriod(paidInterval);
    }

    // Mark this plan as restored so we don't overwrite manual changes later
    restoredPlanIdRef.current = paymentActivatedPlan.avatar_plan_id;
  }, [paymentActivatedPlan, avatarPlans]);

  // Start/stop the simulated-creep interval based on whether a job is running
  useEffect(() => {
    if (!jobId) {
      if (creepIntervalRef.current) {
        clearInterval(creepIntervalRef.current);
        creepIntervalRef.current = null;
      }
      return;
    }
    // Reset bar to 0 when a new job starts
    progressWidthRef.current = 0;
    setProgressWidth(0);

    creepIntervalRef.current = setInterval(() => {
      const current = progressWidthRef.current;
      if (current >= 90) return; // never simulate past 90%
      // Creep slows as it approaches 90
      const gap = 90 - current;
      const increment = Math.max(0.3, gap * 0.015);
      const next = Math.min(90, current + increment);
      progressWidthRef.current = next;
      setProgressWidth(next);
    }, 800);

    return () => {
      if (creepIntervalRef.current) {
        clearInterval(creepIntervalRef.current);
        creepIntervalRef.current = null;
      }
    };
  }, [jobId]);

  // Poll progress endpoint when jobId is set (polling avoids SSE/proxy-buffering issues)
  useEffect(() => {
    if (!jobId) return;
    const apiBase = (process.env.REACT_APP_API_URL || 'https://sayagainservices.sayagain.ai/api').replace(/\/api$/, '');
    const url = `${apiBase}/api/avatars/progress/${jobId}`;
    let cancelled = false;
    let consecutiveFails = 0;
    const MAX_CONSECUTIVE_FAILS = 8;
    const NOT_FOUND_FALLBACK_MS = 90 * 1000; // 90 s of not_found → recovery
    const STALL_TIMEOUT_MS = 3 * 60 * 1000;  // 3 min with no new progress → stall
    let notFoundSince = null;
    // Only starts ticking after the first real progress message is received.
    // null = job not yet seen, so stall check is skipped.
    let lastProgressAt = null;

    const poll = async () => {
      while (!cancelled) {
        // Stall check: job was found and making progress, but has gone silent for 3 minutes.
        if (lastProgressAt !== null && Date.now() - lastProgressAt > STALL_TIMEOUT_MS) {
          if (!cancelled) {
            const avatarName = getValues('firstName') || 'this Avatar';
            setJobId(null);
            setLoading(false);
            toast.error(
              `Something went wrong while creating ${avatarName}'s Avatar. Please try again — any files you've already uploaded have been saved.`,
              { autoClose: 12000 }
            );
          }
          return;
        }

        try {
          const token = localStorage.getItem('access_token');
          const res = await fetch(url, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });

          // Session expired mid-creation
          if (res.status === 401) {
            if (!cancelled) {
              setJobId(null);
              setLoading(false);
              toast.error(
                'Your session expired during creation. Please sign in — your Avatar may already be ready.',
                { autoClose: 8000 }
              );
              setTimeout(() => navigate('/signin'), 2500);
            }
            return;
          }

          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const d = await res.json();
          if (cancelled) break;

          consecutiveFails = 0; // reset on any successful HTTP response

          if (d.status === 'complete' && d.result) {
            progressWidthRef.current = 100;
            setProgressWidth(100);
            setJobId(null);
            setLoading(false);
            handleCreationSuccess(d.result);
            return;
          }

          if (d.status === 'error') {
            setJobId(null);
            setLoading(false);
            toast.error(d.message || 'Avatar creation failed. Please try again.');
            return;
          }

          if (d.status === 'not_found') {
            // Job not in store yet (race on first poll) or server restarted.
            // Keep polling silently. After 90 s, attempt recovery via avatars list.
            if (!notFoundSince) notFoundSince = Date.now();
            if (Date.now() - notFoundSince > NOT_FOUND_FALLBACK_MS) {
              // Recovery check: see if avatar was actually created in the DB
              try {
                const token2 = localStorage.getItem('access_token');
                const currentUserId = userId || sessionStorage.getItem('user_id');
                if (currentUserId && token2) {
                  const avatarRes = await fetch(`${apiBase}/api/avatars?user_id=${currentUserId}`, {
                    headers: { Authorization: `Bearer ${token2}` },
                  });
                  if (avatarRes.ok) {
                    const avatarData = await avatarRes.json();
                    const list = avatarData?.data || avatarData?.avatars || avatarData || [];
                    if (Array.isArray(list) && list.length > 0) {
                      const sorted = [...list].sort(
                        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
                      );
                      const newest = sorted[0];
                      const createdAt = new Date(newest.created_at || 0);
                      if (Date.now() - createdAt.getTime() < 30 * 60 * 1000) {
                        if (!cancelled) {
                          setJobId(null);
                          setLoading(false);
                          navigate('/chat', { replace: true });
                        }
                        return;
                      }
                    }
                  }
                }
              } catch (_) { /* ignore recovery errors */ }

              if (!cancelled) {
                setJobId(null);
                setLoading(false);
                toast.error(
                  'We lost track of your Avatar creation. Please check your Avatars list — it may already be ready.',
                  { autoClose: 10000 }
                );
              }
              return;
            }
            // still within grace period — keep polling silently
          } else if (d.message) {
            notFoundSince = null;       // job found, reset
            lastProgressAt = Date.now(); // backend is alive and reporting
            setProgressMessage(d.message);
            setProgressStage(d.stage || 'reading');
            // Advance bar to the server-reported target if ahead of current simulated position
            const stageTargets = { reading: 33, building: 88 };
            const serverTarget = stageTargets[d.stage] ?? 33;
            if (serverTarget > progressWidthRef.current) {
              progressWidthRef.current = serverTarget;
              setProgressWidth(serverTarget);
            }
          }
        } catch (_) {
          consecutiveFails++;
          if (consecutiveFails >= MAX_CONSECUTIVE_FAILS) {
            if (!cancelled) {
              setJobId(null);
              setLoading(false);
              toast.error(
                'Connection lost during Avatar creation. Please check your Avatars list — it may already be ready.',
                { autoClose: 10000 }
              );
            }
            return;
          }
        }

        // Backoff: 2 s normally, grows on consecutive failures (max 10 s)
        const delay = consecutiveFails === 0
          ? 2000
          : Math.min(2000 * Math.pow(1.5, consecutiveFails), 10000);
        await new Promise((r) => setTimeout(r, delay));
      }
    };

    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // Listen for success message from PayPal popup
  useEffect(() => {
    const handlePaypalMessage = (event) => {
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === 'PAYPAL_SUCCESS') {
        console.log('CreateAvatarPage: Received PayPal success message from popup');

        // Invite modal gift: capture + send-invitation both OK → close modal and open chat
        if (event.data?.inviteModalDoneGoChat) {
          const rawInviteGift = sessionStorage.getItem(INVITE_MODAL_GIFT_EMAILS_KEY);
          if (rawInviteGift) {
            try {
              const emailsLower = new Set(JSON.parse(rawInviteGift));
              sessionStorage.removeItem(INVITE_MODAL_GIFT_EMAILS_KEY);
              setInviteRows((prev) =>
                prev.map((r) => {
                  const el = (r.email || '').trim().toLowerCase();
                  return el && emailsLower.has(el) ? { ...r, disabled: true } : r;
                })
              );
            } catch (e) {
              console.warn('invite_modal_gift merge failed:', e);
              try {
                sessionStorage.removeItem(INVITE_MODAL_GIFT_EMAILS_KEY);
              } catch (_) { /* ignore */ }
            }
          }
          if (inviteModalPaypalCleanupRef.current) inviteModalPaypalCleanupRef.current();
          setShowInviteModal(false);
          navigate('/chat', { replace: true });
          return;
        }

        // Check if this was a gift payment
        const rawGift = sessionStorage.getItem(GIFT_RETURN_CONTEXT_KEY);
        if (rawGift) {
          try {
            const ctx = JSON.parse(rawGift);
            if (ctx?.createdAvatarId && Array.isArray(ctx?.inviteRows)) {
              if (process.env.NODE_ENV === 'development') {
                console.log('Gift success message: merging rows update. Gifted email:', ctx.giftedEmail);
              }
              sessionStorage.removeItem(GIFT_RETURN_CONTEXT_KEY);
              setCreatedAvatarId(ctx.createdAvatarId);
              if (ctx.avatarFirstName) setInviteAvatarName(ctx.avatarFirstName);

              const giftedEmailLower = (ctx.giftedEmail || '').trim().toLowerCase();

              setInviteRows(prev => {
                // If we have rows in state, use those as base. Otherwise use ctx.inviteRows (reload case).
                const baseRows = prev.length > 0 ? prev : ctx.inviteRows;
                return baseRows.map(r => {
                  const rowEmailLower = (r.email || '').trim().toLowerCase();
                  const isGifted = (giftedEmailLower && rowEmailLower === giftedEmailLower);
                  // Check if it was marked disabled in the ctx we saved
                  const wasDisabledInCtx = ctx.inviteRows.find(cr =>
                    cr.id === r.id ||
                    ((cr.email || '').trim().toLowerCase() === rowEmailLower && rowEmailLower !== '')
                  )?.disabled;

                  return (r.disabled || isGifted || wasDisabledInCtx) ? { ...r, disabled: true } : r;
                });
              });

              setShowInviteModal(true);
              return;
            }
          } catch (e) {
            console.error('Failed to handle gift success message:', e);
          }
        }

        // Invite-modal gift without inviteModalDoneGoChat (e.g. send-invitation failed): keep modal open
        const rawInviteGiftLeft = sessionStorage.getItem(INVITE_MODAL_GIFT_EMAILS_KEY);
        if (rawInviteGiftLeft) {
          return;
        }

        // Standard payment success
        if (event.data.data) {
          setPaymentActivatedPlan(event.data.data);
          setShowPlanModal(false);
          setShowPaymentModal(false);
        }
      } else if (event.data?.type === 'PAYPAL_ERROR') {
        console.warn('CreateAvatarPage: Received PayPal error message from popup');
        setProcessingPayment(false);
        sessionStorage.removeItem(GIFT_RETURN_CONTEXT_KEY);
        sessionStorage.removeItem(INVITE_MODAL_GIFT_EMAILS_KEY);
        if (inviteModalPaypalCleanupRef.current) inviteModalPaypalCleanupRef.current();
        toast.error(event.data.error || 'Payment was not confirmed. Please try again or contact support.');
      }
    };

    window.addEventListener('message', handlePaypalMessage);
    return () => window.removeEventListener('message', handlePaypalMessage);
  }, []);

  useEffect(() => {
    return () => {
      if (inviteModalPaypalCleanupRef.current) inviteModalPaypalCleanupRef.current();
    };
  }, []);

  // Restore invite modal when returning from gift payment (PayPal success - fallback for reload)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(GIFT_RETURN_CONTEXT_KEY);
      if (!raw) return;
      const ctx = JSON.parse(raw);
      if (!ctx?.createdAvatarId || !Array.isArray(ctx?.inviteRows)) return;

      // Only apply disabled rows if the payment was actually captured successfully.
      // PaymentSuccessPage sets this flag after a confirmed capture.
      let paymentConfirmed = false;
      try {
        const checkoutRaw = sessionStorage.getItem('paypal_checkout_context') || localStorage.getItem('paypal_checkout_context');
        if (checkoutRaw) {
          const checkoutCtx = JSON.parse(checkoutRaw);
          if (checkoutCtx?.order_id) {
            paymentConfirmed = sessionStorage.getItem(`payment_processed_${checkoutCtx.order_id}`) === 'true';
          }
        }
      } catch (_) { }

      sessionStorage.removeItem(GIFT_RETURN_CONTEXT_KEY);

      if (!paymentConfirmed) {
        // Payment did not complete — discard stale context without disabling rows.
        return;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('Gift reload fallback: merging rows update. Gifted email:', ctx.giftedEmail);
      }
      setCreatedAvatarId(ctx.createdAvatarId);
      if (ctx.avatarFirstName) setInviteAvatarName(ctx.avatarFirstName);

      const giftedEmailLower = (ctx.giftedEmail || '').trim().toLowerCase();

      setInviteRows(prev => {
        // Fallback always starts from empty state usually, so use ctx.inviteRows
        const baseRows = prev.length > 0 ? prev : ctx.inviteRows;
        return baseRows.map(r => {
          const rowEmailLower = (r.email || '').trim().toLowerCase();
          const isGifted = (giftedEmailLower && rowEmailLower === giftedEmailLower);
          const wasDisabledInCtx = ctx.inviteRows.find(cr =>
            cr.id === r.id ||
            ((cr.email || '').trim().toLowerCase() === rowEmailLower && rowEmailLower !== '')
          )?.disabled;

          return (r.disabled || isGifted || wasDisabledInCtx) ? { ...r, disabled: true } : r;
        });
      });

      setShowInviteModal(true);
    } catch (_) { /* ignore */ }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside any dropdown
      const clickedElement = event.target;
      const isInsideDropdown = clickedElement.closest('.invite-action-select-wrapper');
      if (!isInsideDropdown && openDropdownId !== null) {
        setOpenDropdownId(null);
      }
    };

    if (openDropdownId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownId]);

  // Reset invite row actions if plan options change (e.g. stale gift_ ids after API reload).
  useEffect(() => {
    if (!showInviteModal || !avatarPlanDetails) return;
    const allowed = new Set(getDropdownOptions(avatarPlanDetails).map((o) => o.value));
    setInviteRows((prev) => {
      let changed = false;
      const next = prev.map((i) => {
        if (allowed.has(i.action)) return i;
        changed = true;
        return { ...i, action: 'send_invite', plan_id: 1, plan_pricing_id: null };
      });
      return changed ? next : prev;
    });
  }, [showInviteModal, avatarPlanDetails, userData]);


  // Check for signup invite params (modal not shown on create avatar page)
  useEffect(() => {
    const currentUid = getCurrentSessionUserId();
    if (!isAuthenticated || !currentUid || signupInviteProcessed) return;

    const checkSignupInvite = async () => {
      try {
        const raw = localStorage.getItem('signup_invite_params');
        if (!raw) return;

        const parsed = JSON.parse(raw);
        if (!parsed?.invite_id) return;

        setSignupInviteProcessed(true);
        // Confirm Invitation modal not shown on create avatar page
      } catch (err) {
        console.warn('Error processing signup invite params:', err);
      }
    };

    checkSignupInvite();
  }, [isAuthenticated, signupInviteProcessed, userId]);

  // Auto-select default plan when plan modal opens
  useEffect(() => {
    if (!showPlanModal || !avatarPlans.length) return;

    // Find the selected avatar type
    const selectedAvatarType = avatarPlans.find(avatarTypeItem =>
      normalizeAvatarTypeName(avatarTypeItem.name) === normalizeAvatarTypeName(avatarType)
    );
    if (!selectedAvatarType?.plans?.length) return;

    const availablePlans = selectedAvatarType.plans;

    // If a plan is already selected, sync billing period with it
    if (selectedPlanId) {
      const selectedPlan = availablePlans.find(p => Number(p.id) === Number(selectedPlanId));
      if (selectedPlan) {
        // Sync billing period to match the selected plan (only if it doesn't match)
        const isFree = !selectedPlan.monthly_price_usd && !selectedPlan.yearly_price_usd && !selectedPlan.day_price_usd;
        const isMonthly = selectedPlan.monthly_price_usd && !selectedPlan.yearly_price_usd;
        const isYearly = selectedPlan.yearly_price_usd && !selectedPlan.monthly_price_usd;

        if (isFree && billingPeriod !== 'free') {
          setBillingPeriod('free');
        } else if (isMonthly && billingPeriod !== 'monthly') {
          setBillingPeriod('monthly');
        } else if (isYearly && billingPeriod !== 'yearly') {
          setBillingPeriod('yearly');
        }
        return;
      }
    }

    // Select default based on priority: Monthly > Yearly
    const monthlyPlan = availablePlans.find(p => p.monthly_price_usd);
    const yearlyPlan = availablePlans.find(p => p.yearly_price_usd);

    // Select the first available plan in priority order (ignoring Free)
    if (monthlyPlan) {
      setSelectedPlanId(monthlyPlan.id);
      setBillingPeriod('monthly');
    } else if (yearlyPlan) {
      setSelectedPlanId(yearlyPlan.id);
      setBillingPeriod('yearly');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPlanModal, avatarPlans, avatarType, selectedPlanId]);

  // Decide if dropdown should open upward so it stays inside the modal and doesn't overlap content below (e.g. "Drag & Drop").
  // Use the invite list (or modal) as the boundary, and open upward when there's more space above than below.
  const EST_DROPDOWN_HEIGHT = 220;
  const getShouldOpenUpward = (buttonElement) => {
    if (!buttonElement || typeof buttonElement.getBoundingClientRect !== 'function') return false;
    const rect = buttonElement.getBoundingClientRect();
    const scrollContainer =
      buttonElement.closest('.invite-rows-full-list') ||
      buttonElement.closest('.invite-rows-preview') ||
      buttonElement.closest('.invite-emails-list') ||
      null;
    const containerRect = scrollContainer?.getBoundingClientRect?.();
    const containerBottom = containerRect?.bottom ?? window.innerHeight;
    const containerTop = containerRect?.top ?? 0;

    const spaceBelow = containerBottom - rect.bottom;
    const spaceAbove = rect.top - containerTop;
    // Open upward if there's less room below than above, or if below is too small for the menu
    return spaceBelow < Math.max(EST_DROPDOWN_HEIGHT, spaceAbove) || spaceBelow < EST_DROPDOWN_HEIGHT;
  };

  // Fallback: re-check after dropdown is rendered (e.g. if viewport was resized)
  const checkDropdownPosition = (buttonElement, rowId) => {
    if (!buttonElement) return;
    setTimeout(() => {
      const dropdownElement = document.querySelector(`[data-row-id="${rowId}"] .invite-action-dropdown-menu`);
      if (!dropdownElement) return;
      const rect = buttonElement.getBoundingClientRect();
      const dropdownHeight = dropdownElement.offsetHeight || 150;
      const scrollContainer =
        buttonElement.closest('.invite-rows-full-list') ||
        buttonElement.closest('.invite-rows-preview') ||
        buttonElement.closest('.invite-emails-list') ||
        null;
      const containerRect = scrollContainer?.getBoundingClientRect?.();
      const containerBottom = containerRect?.bottom ?? window.innerHeight;
      const containerTop = containerRect?.top ?? 0;

      const spaceBelow = containerBottom - rect.bottom;
      const spaceAbove = rect.top - containerTop;
      const minHeight = Math.max(dropdownHeight + 20, 200);
      const shouldOpenUpward =
        spaceBelow < minHeight || (spaceBelow < spaceAbove && spaceAbove > dropdownHeight);
      setDropdownOpensUpward(prev => ({ ...prev, [rowId]: shouldOpenUpward }));

      // Keep dropdown anchored to the button without changing scroll position.
      // Using fixed positioning avoids forcing the scroll container to overflow: visible (which can reset scrollTop).
      const top = shouldOpenUpward ? (rect.top - dropdownHeight - 4) : (rect.bottom + 4);
      setDropdownPosition(prev => ({
        ...prev,
        [rowId]: { left: rect.left, top: Math.max(8, top), width: rect.width }
      }));
    }, 10);
  };

  // Helper function to get icon for an option
  const getOptionIcon = (value) => {
    if (value === 'send_invite') {
      return (
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      );
    } else if (value !== 'send_invite' && String(value).startsWith('gift_')) {
      return (
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
        </svg>
      );
    }
    return null;
  };

  // Fetch user ID once and cache it - prevents multiple API calls
  const fetchUserOnce = async (forceRefetch = false) => {
    // Check if already fetched in this session (across page navigations)
    const userFetchedInSession = sessionStorage.getItem('user_fetched');
    const storedUserId = sessionStorage.getItem('user_id');
    const storedUserData = sessionStorage.getItem('user_data');

    // If already fetched/cached, try to restore state
    if (!forceRefetch && userFetchedInSession && storedUserId) {
      console.log('CreateAvatarPage: User already fetched in this session, checking cache...');

      const cachedUserId = parseInt(storedUserId, 10);
      setUserId(cachedUserId);

      // Restore full user data if available
      if (storedUserData) {
        try {
          const parsedUserData = JSON.parse(storedUserData);
          setUserData(parsedUserData);
          console.log('CreateAvatarPage: Restored userData from sessionStorage');
          if (hasActiveSubscription(parsedUserData)) {
            return cachedUserId;
          }
          console.log('CreateAvatarPage: Cached userData missing active subscription, refetching...');
        } catch (e) {
          console.warn('Failed to parse cached user_data, will refetch');
        }
      } else {
        // If we have userId but not full userData, we might want to fetch if we need the name
        // But to respect the 'active session' logic, we'll return userId and let a background effect fetch if needed, 
        // OR just proceed to fetch here if we really need userData.
        // For the subscription card, we need `userData`. Let's fall through to fetch if userData is missing.
        console.log('CreateAvatarPage: Missing cached userData, proceeding to fetch.');
      }
    }

    // Double check to prevent duplicate calls (React StrictMode or re-renders)
    // Only skip if we have BOTH userId and userData (since we need both now)
    if (!forceRefetch && userFetchedRef.current && userId && userData && hasActiveSubscription(userData)) {
      console.log('CreateAvatarPage: User data already fully loaded, skipping API call');
      return userId;
    }

    try {
      // Set flags immediately to prevent duplicate calls (synchronous)
      userFetchedRef.current = true;
      sessionStorage.setItem('user_fetched', 'true'); // Mark in session immediately
      console.log('CreateAvatarPage: Fetching user data from API...');

      const userResponse = await authAPI.getCurrentUser();
      if (userResponse.status && userResponse.data) {
        const fetchedUserData = userResponse.data;
        const fetchedUserId = fetchedUserData.id || fetchedUserData.user_id;

        // Cache the full user data and userId
        setUserData(fetchedUserData);
        setUserId(fetchedUserId);
        sessionStorage.setItem('user_id', fetchedUserId.toString());
        sessionStorage.setItem('user_data', JSON.stringify(fetchedUserData)); // Cache full data

        // Match user's plan/avatar_type to API avatar types when plans are loaded (handled in useEffect)
        const userAvatarType = fetchedUserData.avatar_type || fetchedUserData.plan_type || fetchedUserData.plan_name;
        if (userAvatarType) setUserPlanType(userAvatarType.toLowerCase());

        console.log('CreateAvatarPage: User data fetched and cached. UserId:', fetchedUserId);
        return fetchedUserId;
      }
    } catch (error) {
      console.error('Error fetching user ID:', error);
      // Reset refs on error so it can retry if needed
      if (error?.status !== 401) { // Don't reset on auth errors
        userFetchedRef.current = false;
        sessionStorage.removeItem('user_fetched');
        sessionStorage.removeItem('user_id');
        sessionStorage.removeItem('user_data');
      }
      throw error;
    }
    return null;
  };

  // Removed fetchUserPlanType - logic is now integrated into fetchUserOnce to prevent duplicate API calls

  const normalizeSelectedPlan = (plan, selectedInterval = billingPeriod) => {
    if (!plan) return null;

    // Determine interval based on selectedInterval or plan properties
    let interval;
    if (selectedInterval === 'free' || (!plan.monthly_price_usd && !plan.yearly_price_usd && !plan.day_price_usd)) {
      interval = 'free_trial';
    } else if (selectedInterval === 'monthly' || plan.monthly_price_usd) {
      interval = 'monthly';
    } else if (selectedInterval === 'yearly' || plan.yearly_price_usd) {
      interval = 'yearly';
    } else {
      interval = plan.interval_name || plan.interval || plan.plan_interval || 'free_trial';
    }

    let priceUsd = 0;
    if (interval === 'monthly') {
      priceUsd = Number(plan.monthly_price_usd ?? plan.price_usd ?? 0);
    } else if (interval === 'yearly') {
      priceUsd = Number(plan.yearly_price_usd ?? plan.price_usd ?? 0);
    } else {
      // Free trial or other intervals
      priceUsd = Number(plan.price_usd ?? plan.day_price_usd ?? 0);
    }

    return {
      avatar_plan_id: Number(plan.avatar_plan_id ?? plan.plan_id ?? plan.id),
      interval,
      price_usd: Number.isFinite(priceUsd) ? priceUsd : 0,
      token_limit: Number(plan.token_limit ?? plan.tokens_limit ?? plan.max_tokens ?? 0),
      plan_days: Number(plan.plan_days ?? plan.days ?? 0),
      storage: plan.max_storage_gb ?? plan.storage ?? plan.storage_limit ?? plan.storage_limit_gb ?? null,
      family_members: Number(plan.max_family_members ?? plan.family_members ?? plan.family_member_limit ?? plan.members_limit ?? 0),
    };
  };

  const getCurrentSelectedPlan = () => {
    const selectedAvatarType = avatarPlans.find(
      (avatarTypeItem) => normalizeAvatarTypeName(avatarTypeItem.name) === normalizeAvatarTypeName(avatarType)
    );
    if (!selectedAvatarType?.plans?.length) return null;
    const chosen = selectedAvatarType.plans.find((p) => Number(p.id) === Number(selectedPlanId));
    if (!chosen) return null;
    return normalizeSelectedPlan(chosen, billingPeriod);
  };

  const isSelectedPlanActivated = () => {
    const current = getCurrentSelectedPlan();
    if (!current) return false;
    if (current.price_usd <= 0) return true;
    if (!paymentActivatedPlan) return false;
    return (
      Number(paymentActivatedPlan.avatar_plan_id) === Number(current.avatar_plan_id) &&
      String(paymentActivatedPlan.plan_interval) === String(current.interval)
    );
  };

  // Fetch avatar plans from API
  const fetchAvatarPlans = async () => {
    setLoadingPlans(true);
    try {
      let response;
      try {
        // Checkout flow source (required endpoint): GET /api/avatar-plans
        response = await plansAPI.getAvatarPlans();
      } catch (checkoutErr) {
        console.warn('GET /api/avatar-plans failed, falling back to legacy plans endpoint:', checkoutErr);
        response = await plansAPI.getAvatarPlans();
      }

      // Handle API response structure: { status, data: { avatar_types: [...] } }
      let avatarTypes = [];

      if (response && response.data && response.data.avatar_types && Array.isArray(response.data.avatar_types)) {
        avatarTypes = response.data.avatar_types;
      } else if (response && response.data && Array.isArray(response.data)) {
        avatarTypes = response.data;
      } else if (Array.isArray(response)) {
        avatarTypes = response;
      }

      if (avatarTypes.length > 0) {
        setAvatarPlans(avatarTypes);

        // If we already restored a payment draft, do not override it with defaults/suggestions.
        if (hasRestoredPaymentDraftRef.current) {
          setSuggestionFlag(false);
          return;
        }

        // Check for suggested plan (suggestion_flag: true) across all avatar types
        let suggestedPlan = null;
        let suggestedAvatarType = null;

        for (const avatarTypeItem of avatarTypes) {
          if (avatarTypeItem.plans && Array.isArray(avatarTypeItem.plans)) {
            const planWithSuggestion = avatarTypeItem.plans.find(plan => plan.suggestion_flag === true);
            if (planWithSuggestion) {
              suggestedPlan = planWithSuggestion;
              suggestedAvatarType = avatarTypeItem;
              break;
            }
          }
        }

        if (suggestedPlan && suggestedAvatarType) {
          // Set suggestion flag to true
          setSuggestionFlag(true);

          // Set avatar type to the one containing the suggested plan
          setAvatarType(suggestedAvatarType.name.toLowerCase());

          // Set billing period based on the suggested plan
          if (!suggestedPlan.monthly_price_usd && !suggestedPlan.yearly_price_usd && !suggestedPlan.day_price_usd) {
            setBillingPeriod('free');
          } else if (suggestedPlan.monthly_price_usd && !suggestedPlan.yearly_price_usd) {
            setBillingPeriod('monthly');
          } else if (suggestedPlan.yearly_price_usd && !suggestedPlan.monthly_price_usd) {
            setBillingPeriod('yearly');
          }

          // Set the selected plan ID
          setSelectedPlanId(suggestedPlan.id);

          console.log('Applied suggested plan:', {
            planId: suggestedPlan.id,
            avatarType: suggestedAvatarType.name,
            billingPeriod: suggestedPlan.monthly_price_usd ? 'monthly' : 'yearly',
            monthlyPrice: suggestedPlan.monthly_price_usd,
            yearlyPrice: suggestedPlan.yearly_price_usd
          });
        } else {
          // No suggested plan, use default behavior
          setSuggestionFlag(false);

          // Set default avatar type to first avatar type
          const firstAvatarType = avatarTypes[0];
          if (firstAvatarType.name) {
            setAvatarType(firstAvatarType.name.toLowerCase());
            // If it has a plan, set the first plan as default
            if (firstAvatarType.plans && firstAvatarType.plans.length > 0) {
              setSelectedPlanId(firstAvatarType.plans[0].id);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching avatar plans:', error);
      // Only show error if it's not a network/timeout issue or if it's a critical error
      // Don't show error on page refresh if it's just a temporary network issue
      const isNetworkError = !error?.status && (error?.message?.includes('network') || error?.message?.includes('fetch') || error?.message?.includes('timeout'));
      const isServerError = error?.status >= 500;

      // Only show error toast for non-network issues or persistent server errors
      // Silent fail for network issues on page load to avoid annoying users
      if (!isNetworkError && !isServerError) {
        toast.error('Failed to load avatar plans. Using default options.', {
          toastId: 'avatar-plans-error',
          autoClose: 3000
        });
      } else {
        // Log silently for network/server errors - default options will be used
        console.warn('Avatar plans fetch failed (network/server issue). Using default options silently.');
      }
    } finally {
      setLoadingPlans(false);
    }
  };

  // Fetch relations from API
  const fetchRelations = async () => {
    setLoadingRelations(true);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        console.warn('No authentication token found for fetching relations');
        return;
      }

      const apiUrl = (process.env.REACT_APP_API_URL || 'https://sayagainservices.sayagain.ai/api').replace(/\/api$/, '');
      const response = await fetch(`${apiUrl}/api/avatars/relations`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to load relations:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        toast.error(`Failed to load relations: ${response.status} ${response.statusText}`);
        return;
      }

      const data = await response.json();
      console.log('Relations API response:', data);

      // Extract relations from response
      // Response format: { status: true, status_code: 200, data: { relations: [{ value, label }, ...] } }
      let relationsData = [];
      if (data.data && data.data.relations && Array.isArray(data.data.relations)) {
        relationsData = data.data.relations;
      } else if (data.data && Array.isArray(data.data)) {
        relationsData = data.data;
      } else if (data.relations && Array.isArray(data.relations)) {
        relationsData = data.relations;
      } else if (Array.isArray(data)) {
        relationsData = data;
      }

      console.log('Extracted relations:', relationsData.length, 'relations');
      setRelations(relationsData);
    } catch (error) {
      console.error('Error fetching relations:', error);
      toast.error('Failed to load relations. Please try again.', {
        toastId: 'relations-load-error',
        autoClose: 3000
      });
    } finally {
      setLoadingRelations(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  // Recursively read all files from a directory entry; returns { file, relativePath } so each file shows as a separate row with path
  const readAllFilesFromDirectory = async (dirEntry, basePath = '') => {
    const result = [];
    const reader = dirEntry.createReader();
    const readBatch = () =>
      new Promise((resolve, reject) => {
        reader.readEntries(resolve, reject);
      });
    let entries = [];
    do {
      entries = await readBatch();
      for (const entry of entries) {
        const fullPath = basePath ? `${basePath}/${entry.name}` : entry.name;
        if (entry.isFile) {
          const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
          if (file && file.name && (file.size === undefined || file.size >= 0)) {
            result.push({ file, relativePath: fullPath });
          }
        } else if (entry.isDirectory) {
          const subResults = await readAllFilesFromDirectory(entry, fullPath);
          result.push(...subResults);
        }
      }
    } while (entries.length > 0);
    return result;
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);

    const items = e.dataTransfer?.items;
    const collected = []; // array of File or { file, displayPath }

    if (items && typeof items[0]?.webkitGetAsEntry === 'function') {
      for (const item of Array.from(items)) {
        if (item.kind !== 'file') continue;
        const entry = item.webkitGetAsEntry();
        if (!entry) {
          const file = item.getAsFile();
          if (file && file.name && file.size > 0) collected.push(file);
          continue;
        }
        if (entry.isDirectory) {
          try {
            const dirResults = await readAllFilesFromDirectory(entry, entry.name);
            collected.push(...dirResults);
          } catch (err) {
            console.warn('Could not read dropped folder:', entry.name, err);
            toast.warning(`Could not read folder "${entry.name}". Use "Select Folder" instead.`, {
              toastId: 'folder-read-error',
              autoClose: 5000
            });
          }
        } else {
          const file = item.getAsFile();
          if (file && file.name && file.size > 0) collected.push(file);
        }
      }
    }

    const fallbackFiles = Array.from(e.dataTransfer.files || []).filter(
      (file) => file && file.name && file.size > 0
    );
    if (collected.length === 0) {
      if (fallbackFiles.length === 0) {
        toast.warning('No files detected. Drag files or use "Select Folder" to add one or more folders.', {
          toastId: 'no-files-dropped',
          autoClose: 4000
        });
        return;
      }
      addFiles(fallbackFiles);
      return;
    }
    const allPlainFiles = collected.length > 0 && collected.every((c) => c instanceof File);
    if (allPlainFiles && fallbackFiles.length > collected.length) {
      addFiles(fallbackFiles);
      return;
    }
    addFiles(collected);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) {
      console.warn('No files selected in file input');
      return;
    }

    const validFiles = files.filter((file) => file && file.name);
    if (validFiles.length === 0) {
      e.target.value = '';
      return;
    }

    // When user selects a folder, pass each file with its path so they show as separate rows (not under one folder)
    const isFolderSelection = validFiles[0]?.webkitRelativePath && validFiles[0].webkitRelativePath.includes('/');
    const toAdd = isFolderSelection
      ? validFiles.map((file) => ({ file, displayPath: file.webkitRelativePath || file.name }))
      : validFiles;

    addFiles(toAdd);
    e.target.value = '';
  };



  // Normalize input: accept File[] or { file, displayPath }[] (displayPath = path for folder files so each shows as separate row)
  const normalizeFileInput = (files) => {
    return files.map((item) => {
      if (item && typeof item === 'object' && item.file instanceof File) {
        return { file: item.file, displayName: item.displayPath || item.relativePath || item.file.name };
      }
      if (item instanceof File) {
        return { file: item, displayName: item.name };
      }
      return null;
    }).filter(Boolean);
  };

  const addFiles = (files) => {
    if (!files || files.length === 0) {
      console.warn('No files provided to addFiles');
      return;
    }

    const normalized = normalizeFileInput(files);

    // Skip video/image/audio and XLS/EXE — single common message for all unsupported files
    const skipped = normalized.filter((n) => isVideoImageOrAudioFile(n.file) || isBlockedFile(n.file));
    const toProcess = normalized.filter((n) => !isVideoImageOrAudioFile(n.file) && !isBlockedFile(n.file));
    if (skipped.length > 0) {
      toast.info(
        skipped.length === 1
          ? `"${skipped[0].file.name}" was not added. Video, image, audio, XLS and EXE files are not supported.`
          : `${skipped.length} file(s) were not added. Video, image, audio, XLS and EXE files are not supported.`,
        { toastId: 'unsupported-files-skipped', autoClose: 5000 }
      );
    }

    if (toProcess.length === 0) {
      return;
    }

    console.log('addFiles called with', toProcess.length, 'file(s)');
    console.log('Current actualFiles count:', actualFiles.length);
    console.log('Current uploadedFiles count:', uploadedFiles.length);

    // Get storage limit from selectedPlan or default to 5 GB
    const maxStorageGB = Number(selectedPlan?.storage || 5);
    const MAX_TOTAL_SIZE = maxStorageGB * 1024 * 1024 * 1024; // in bytes

    // Dedupe by displayName + size so folder files (with path) are separate from each other and from same-name files elsewhere
    const duplicateFiles = [];
    const uniqueItems = [];

    toProcess.forEach(({ file, displayName }) => {
      if (!file || !file.name) {
        console.warn('Invalid file object:', file);
        return;
      }
      const isDuplicate = uploadedFiles.some(
        (_, i) => uploadedFiles[i]?.name === displayName && actualFiles[i]?.size === file.size
      );
      if (isDuplicate) {
        duplicateFiles.push({ file, displayName });
      } else {
        uniqueItems.push({ file, displayName });
      }
    });

    if (duplicateFiles.length > 0) {
      const names = duplicateFiles.map((d) => d.displayName).join(', ');
      toast.warning(
        duplicateFiles.length === 1
          ? `That file is already in your list.`
          : `${duplicateFiles.length} files are already in your list and were skipped.`,
        { toastId: 'duplicate-files', autoClose: 5000 }
      );
    }

    if (uniqueItems.length === 0) {
      return;
    }

    const currentTotalSize = actualFiles.reduce((total, file) => total + file.size, 0);
    const newFilesTotalSize = uniqueItems.reduce((total, { file }) => total + file.size, 0);
    const totalSizeAfterAdd = currentTotalSize + newFilesTotalSize;

    if (totalSizeAfterAdd > MAX_TOTAL_SIZE) {
      const currentTotalGB = (currentTotalSize / (1024 * 1024 * 1024)).toFixed(2);
      const newFilesGB = (newFilesTotalSize / (1024 * 1024 * 1024)).toFixed(2);
      const totalGB = (totalSizeAfterAdd / (1024 * 1024 * 1024)).toFixed(2);
      toast.error(
        `Total file size exceeds ${maxStorageGB} GB limit!\nCurrent: ${currentTotalGB} GB\nNew files: ${newFilesGB} GB\nTotal: ${totalGB} GB\nPlease remove some files or select smaller files.`,
        { toastId: 'file-size-limit', autoClose: 8000 }
      );
      return;
    }

    // Cap: 50 files and 200MB total
    const MAX_FILES_ALLOWED = 50;
    const MAX_UPLOAD_BYTES_ALLOWED = 200 * 1024 * 1024;
    const totalFilesAfterAdd = actualFiles.length + uniqueItems.length;
    if (totalFilesAfterAdd > MAX_FILES_ALLOWED || totalSizeAfterAdd > MAX_UPLOAD_BYTES_ALLOWED) {
      setFileLimitFileCount(totalFilesAfterAdd);
      setShowFileLimitModal(true);
      return;
    }

    const baseId = Date.now();
    const newFiles = uniqueItems.map(({ file, displayName }, index) => ({
      id: `file-${baseId}-${index}-${Math.random().toString(36).slice(2, 11)}`,
      name: displayName,
      size: formatFileSize(file.size),
      type: getFileType(file.name),
      file
    }));

    const uniqueFiles = uniqueItems.map((i) => i.file);
    console.log('Adding unique files to state:', uniqueItems.map((i) => i.displayName));

    // Update states and extract emails from ONLY the new unique files (merge with existing)
    // Store actual file objects for upload (these will be sent to API with field name 'files')
    // Each file is appended with the same field name 'files' to match cURL: -F "files=@file1" -F "files=@file2"
    try {
      setActualFiles(prev => {
        const updated = [...prev, ...uniqueFiles];
        console.log('Updated actualFiles count:', updated.length);
        return updated;
      });

      setUploadedFiles(prev => {
        const updated = [...prev, ...newFiles];
        console.log('Updated uploadedFiles count:', updated.length);
        return updated;
      });

      // Extract-emails runs only when user clicks Create Avatar (not when adding files; avatar-plan flow stays separate)

      // Show success message for unique files
      if (uniqueFiles.length > 0) {
      }
    } catch (error) {
      console.error('Error adding files:', error);
      toast.error('Failed to add files. Please try again.', {
        toastId: 'files-add-error',
        autoClose: 5000
      });
    }
  };

  const removeFile = async (fileId) => {
    const fileToRemove = uploadedFiles.find(f => f.id === fileId);
    if (fileToRemove && fileToRemove.file) {
      setActualFiles(prev => prev.filter(f => f !== fileToRemove.file));
    }

    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    toast.info('File removed', { toastId: 'file-removed' });
  };

  const toggleInviteRow = (rowId) => {
    setInviteRows(prev => prev.map(r => {
      if (r.id === rowId && r.disabled) return r;
      // Name-only contacts require an email before they can be selected
      if (r.id === rowId && r.name_only && !(r.email || '').trim()) return r;
      return r.id === rowId ? { ...r, selected: !r.selected } : r;
    }));
  };

  // Fetch avatar plan details when dropdown is opened.
  // Returns plan data so callers can use it immediately (React state updates are async).
  const fetchAvatarPlan = async () => {
    if (avatarPlanDetails) return avatarPlanDetails;
    setLoadingAvatarPlan(true);
    try {
      const planResponse = await avatarsAPI.getAvatarPlan();
      if (planResponse.status && planResponse.data) {
        setAvatarPlanDetails(planResponse.data);
        return planResponse.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching avatar plan:', error);
      return null;
    } finally {
      setLoadingAvatarPlan(false);
    }
  };

  // Extract pricing options from API response (supports both flat pricing_options and nested avatar_types)
  const getPricingOptionsFromPlan = (planDataOverride) => {
    const details = planDataOverride ?? avatarPlanDetails;
    if (!details) return [];
    const avatarTypes = details.avatar_types || [];
    if (avatarTypes.length > 0) {
      const all = [];
      avatarTypes.forEach((at) => {
        const opts = at.pricing_options || [];
        opts.forEach((opt) => {
          all.push({
            ...opt,
            avatar_type_id: at.avatar_type_id ?? at.id,
            avatar_type_name: at.name ?? at.type_name ?? '',
          });
        });
      });
      return all;
    }
    return details.pricing_options || [];
  };

  const intervalHintsForInvite = (opt) => {
    const parts = [
      opt?.interval_name,
      opt?.interval,
      opt?.plan_interval,
      opt?.billing_interval,
      opt?.billing_period,
      opt?.billing_cycle,
      opt?.period,
    ];
    return parts
      .filter((p) => p != null && p !== '')
      .map((p) => String(p).toLowerCase().replace(/_/g, ' '))
      .join(' ');
  };

  const classifyInvitePlanInterval = (opt) => {
    const h = intervalHintsForInvite(opt);
    const pd = Number(opt?.plan_days);
    if (Number.isFinite(pd)) {
      if (pd >= 28 && pd <= 35) return 'monthly';
      if (pd >= 360 && pd <= 375) return 'yearly';
    }
    if (h.includes('free') && h.includes('trial')) return null;
    if (/\bmonth\b|monthly|per month|^1m\b/.test(h) || h.includes('month')) return 'monthly';
    if (/\byear\b|yearly|annual|\b12m\b|365|per year/.test(h) || h.includes('year') || h.includes('annual')) {
      return 'yearly';
    }
    return null;
  };

  const invitePlanPricingIdFromOpt = (opt) => {
    if (!opt) return null;
    const toNum = (v) => {
      if (v == null || v === '') return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    return toNum(opt.plan_pricing_id) ?? toNum(opt.pricing_id) ?? toNum(opt.id);
  };

  const isInvitePaidGiftPricingOpt = (opt) => {
    const pid = invitePlanPricingIdFromOpt(opt);
    if (pid == null) return false;
    const h = intervalHintsForInvite(opt);
    if (h.includes('free') && h.includes('trial')) return false;
    if (String(opt?.interval_name || '').toLowerCase() === 'free_trial') return false;
    return true;
  };

  const isInviteGiftAction = (action) =>
    action != null && action !== 'send_invite' && String(action).startsWith('gift_');

  /** Invite modal dropdown: Send invite + one row per paid pricing option (same as chat invite flow). */
  const getDropdownOptions = (planDataOverride) => {
    const options = [];
    const pricingOptions = getPricingOptionsFromPlan(planDataOverride);
    const sendInviteOpt =
      pricingOptions.find(
        (opt) =>
          String(opt?.interval_name || '').toLowerCase() === 'free_trial' || Number(opt?.plan_id) === 1
      ) || pricingOptions[0];
    options.push({
      value: 'send_invite',
      label: 'Send Invite',
      plan_id: 1,
      plan_pricing_id: sendInviteOpt ? invitePlanPricingIdFromOpt(sendInviteOpt) : null,
      interval: null,
    });

    if (isBetaInviter) {
      return options;
    }

    const seenPid = new Set();
    const paidRows = pricingOptions.filter((opt) => {
      const pid = invitePlanPricingIdFromOpt(opt);
      if (pid == null || seenPid.has(pid)) return false;
      if (!isInvitePaidGiftPricingOpt(opt)) return false;
      seenPid.add(pid);
      return true;
    });

    paidRows.sort((a, b) => {
      const ta = a.avatar_type_id ?? 0;
      const tb = b.avatar_type_id ?? 0;
      if (ta !== tb) return ta - tb;
      const ca = classifyInvitePlanInterval(a) === 'monthly' ? 0 : 1;
      const cb = classifyInvitePlanInterval(b) === 'monthly' ? 0 : 1;
      if (ca !== cb) return ca - cb;
      return invitePlanPricingIdFromOpt(a) - invitePlanPricingIdFromOpt(b);
    });

    paidRows.forEach((opt) => {
      const pid = invitePlanPricingIdFromOpt(opt);
      const kind = classifyInvitePlanInterval(opt);
      const intervalLabel =
        kind === 'monthly' ? '1 month' : kind === 'yearly' ? '1 year' : 'plan';
      const price = Number(opt.price_usd);
      const typeBit = opt.avatar_type_name ? `${opt.avatar_type_name} — ` : '';
      options.push({
        value: `gift_${pid}`,
        label: `${typeBit}Gift ${intervalLabel}`,
        plan_id: opt.plan_id != null ? Number(opt.plan_id) : 1,
        plan_pricing_id: pid,
        price_usd: Number.isFinite(price) ? price : null,
        interval: kind,
      });
    });

    return options;
  };

  const sumGiftInvitePrices = (giftRowsResolved, dropdownOpts) => {
    let sum = 0;
    for (const r of giftRowsResolved) {
      const opt = dropdownOpts.find((o) => o.value === r.action);
      const p = Number(opt?.price_usd);
      if (Number.isFinite(p)) sum += p;
    }
    return sum;
  };

  const buildInviteGiftPaypalFromPlan = (planData, giftRows, dropdownOpts) => {
    const pricingOptions = getPricingOptionsFromPlan(planData);

    const unitForGiftRow = (g) => {
      const pid = Number(g.plan_pricing_id);
      const sel = dropdownOpts.find((o) => o.value === g.action);
      let u = Number(sel?.price_usd);
      if (Number.isFinite(u)) return u;
      const po = pricingOptions.find((o) => invitePlanPricingIdFromOpt(o) === pid);
      u = po ? Number(po.price_usd) : NaN;
      return u;
    };

    const intervalForGiftRow = (g) => {
      const pid = Number(g.plan_pricing_id);
      const po = pricingOptions.find((o) => invitePlanPricingIdFromOpt(o) === pid);
      return (
        g.plan_interval || (po ? classifyInvitePlanInterval(po) : null) || 'monthly'
      );
    };

    let dropdownSum = 0;
    giftRows.forEach((g) => {
      const u = unitForGiftRow(g);
      if (Number.isFinite(u)) dropdownSum += u;
    });
    dropdownSum = Math.round(dropdownSum * 100) / 100;

    const byPid = new Map();
    giftRows.forEach((g) => {
      const pid = Number(g.plan_pricing_id);
      if (!Number.isFinite(pid)) return;
      const u = unitForGiftRow(g);
      const interval = intervalForGiftRow(g);
      if (!byPid.has(pid)) {
        byPid.set(pid, { quantity: 0, unit_amount_usd: u, interval });
      } else {
        const meta = byPid.get(pid);
        meta.quantity += 1;
        if (!Number.isFinite(meta.unit_amount_usd) && Number.isFinite(u)) {
          meta.unit_amount_usd = u;
        }
        return;
      }
      byPid.get(pid).quantity += 1;
    });

    const groupedLines = [];
    for (const [pidKey, meta] of byPid) {
      const pid = Number(pidKey);
      const u = Number(meta.unit_amount_usd);
      if (!Number.isFinite(u)) continue;
      groupedLines.push({
        interval: meta.interval || 'monthly',
        quantity: meta.quantity,
        unit_amount_usd: u,
        plan_pricing_id: pid,
      });
    }
    let groupedTotal = groupedLines.reduce(
      (s, li) => s + Number(li.quantity) * Number(li.unit_amount_usd),
      0
    );
    groupedTotal = Math.round(groupedTotal * 100) / 100;

    let gift_line_items = groupedLines;
    let combinedGiftUsd = groupedTotal;

    if (dropdownSum > 0 && Math.abs(groupedTotal - dropdownSum) > 0.02) {
      gift_line_items = giftRows
        .map((g) => {
          const pid = Number(g.plan_pricing_id);
          if (!Number.isFinite(pid)) return null;
          const u = unitForGiftRow(g);
          if (!Number.isFinite(u)) return null;
          return {
            interval: intervalForGiftRow(g),
            quantity: 1,
            unit_amount_usd: u,
            plan_pricing_id: pid,
          };
        })
        .filter(Boolean);
      combinedGiftUsd =
        Math.round(
          gift_line_items.reduce(
            (s, li) => s + Number(li.quantity) * Number(li.unit_amount_usd),
            0
          ) * 100
        ) / 100;
    }

    return { gift_line_items, combinedGiftUsd };
  };

  const setInviteRowAction = (rowId, action) => {
    const currentRow = inviteRows.find((r) => r.id === rowId);
    if (!currentRow) return;

    if (process.env.NODE_ENV === 'development') {
      console.log(`Changing action for row ${rowId} (${currentRow.email}):`, {
        oldAction: currentRow.action,
        newAction: action,
        fullRow: currentRow,
      });
    }

    const planData = avatarPlanDetails;
    const dropdownOptions = getDropdownOptions(planData);
    const selectedOption = dropdownOptions.find((opt) => opt.value === action);

    let planPricingId = selectedOption?.plan_pricing_id;
    let planId = selectedOption?.plan_id ?? (action === 'send_invite' ? 1 : null);

    if (planPricingId == null && isInviteGiftAction(action)) {
      const m = /^gift_(\d+)$/.exec(String(action));
      if (m) planPricingId = Number(m[1]);
    }

    const pricingOpts = getPricingOptionsFromPlan(planData);
    const paidOpt =
      planPricingId != null
        ? pricingOpts.find(
          (opt) => invitePlanPricingIdFromOpt(opt) === Number(planPricingId)
        )
        : null;
    const priceUsd =
      selectedOption?.price_usd != null && Number.isFinite(Number(selectedOption.price_usd))
        ? Number(selectedOption.price_usd)
        : paidOpt != null && Number.isFinite(Number(paidOpt.price_usd))
          ? Number(paidOpt.price_usd)
          : null;

    const newRow = {
      ...currentRow,
      action,
      plan_id: planId ?? currentRow.plan_id ?? 1,
      plan_pricing_id: planPricingId ?? currentRow.plan_pricing_id ?? null,
      ...(priceUsd != null && Number.isFinite(priceUsd) ? { price_usd: priceUsd } : {}),
    };

    setInviteRows((prev) => prev.map((r) => (r.id === rowId ? newRow : r)));
  };

  const removeInviteRow = (rowId) => {
    setInviteRows(prev => prev.filter(r => r.id !== rowId));
  };

  const updateInviteRowField = (rowId, field, value) => {
    setInviteRows(prev =>
      prev.map(r => (r.id === rowId ? { ...r, [field]: value } : r))
    );
  };

  const addBlankInviteRow = () => {
    const newRowId = `manual-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setInviteRows(prev => [
      ...prev,
      {
        id: newRowId,
        name: '',
        email: '',
        selected: true,
        action: 'send_invite', // Default to Send Invite
        plan_id: 1, // plan_id 1 = Send Invitation
        plan_pricing_id: null, // Send Invite doesn't need plan_pricing_id
        source: 'manual',
        isManual: true,
        isEditing: true,
      }
    ]);

    // Scroll to the new row after a brief delay to allow DOM update
    setTimeout(() => {
      const newRowElement = document.querySelector(`[data-row-id="${newRowId}"]`);
      if (newRowElement) {
        newRowElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        // Focus on the name input
        const nameInput = newRowElement.querySelector('.invite-row-name-input');
        if (nameInput) {
          nameInput.focus();
        }
      }
    }, 100);
  };

  const buildSelectedEmailRecipients = (rows, planData) => {
    const pricingOptions = getPricingOptionsFromPlan(planData);
    const dropdownOptions = getDropdownOptions(planData);
    return rows.map((r) => {
      const email = (r.email || '').trim();
      const action = r.action || 'send_invite';
      const sel = dropdownOptions.find((opt) => opt.value === action);
      let plan_id = sel?.plan_id ?? 1;
      let plan_pricing_id = sel?.plan_pricing_id;
      if (action === 'send_invite' && (plan_pricing_id === null || plan_pricing_id === undefined)) {
        const fallback =
          pricingOptions.find((opt) => String(opt?.interval_name || '').toLowerCase() === 'free_trial') ||
          pricingOptions[0];
        plan_pricing_id = fallback ? invitePlanPricingIdFromOpt(fallback) : null;
      }
      if (isInviteGiftAction(action) && (plan_pricing_id == null) && r.plan_pricing_id != null) {
        plan_pricing_id = r.plan_pricing_id;
      }
      if ((!plan_id || !Number.isFinite(Number(plan_id))) && r.plan_id) plan_id = r.plan_id;
      if ((plan_pricing_id == null || plan_pricing_id === '') && r.plan_pricing_id != null) {
        plan_pricing_id = r.plan_pricing_id;
      }
      const recipient = {
        email,
        plan_id: Number(plan_id) || 1,
      };
      if (r.name && r.name.trim()) recipient.name = r.name.trim();
      if (plan_pricing_id != null && plan_pricing_id !== '') {
        recipient.plan_pricing_id = Number(plan_pricing_id);
      }
      return recipient;
    });
  };

  // Send invite emails after avatar creation
  // This endpoint ONLY accepts emails that were extracted from the avatar creation (extracted_emails)
  // Flow: create avatar -> response includes extracted_emails -> user selects emails -> call this endpoint
  // Send-invite rows → POST /api/avatars/{id}/invite-emails (modal stays open).
  // Gift rows → PayPal popup; after capture, PaymentSuccessPage calls POST /api/projects/send-invitation (bulk).
  const handleSendInviteEmails = async () => {
    // Allowed recipients: extracted from create-avatar response OR manually added via "+ Add someone".
    // Historical note: some rows use source === 'extract' (extracted_emails), others use source === 'create'.
    const selectedRows = inviteRows.filter((r) => r.selected);
    const enabledRows = selectedRows.filter((r) => !r.disabled);

    // If only already-sent (disabled) rows are selected, just finish the flow
    if (selectedRows.length > 0 && enabledRows.length === 0) {
      handleSkipInvites();
      return;
    }

    let allowedRows = enabledRows.filter(
      (r) => r.source === 'create' || r.source === 'extract' || r.source === 'manual' || r.isManual === true || r.name_only === true
    );

    // Silently drop rows where both name and email are blank/whitespace (empty manual rows)
    allowedRows = allowedRows.filter(
      (r) => (r.name || '').trim() || (r.email || '').trim()
    );

    // Error on partial rows: name filled but no email, or email filled but no name
    const partialRows = allowedRows.filter(
      (r) => !(r.name || '').trim() || !(r.email || '').trim()
    );
    if (partialRows.length > 0) {
      toast.error('Please fill in both a name and email for each recipient, or remove the incomplete row.', {
        toastId: 'invite-partial-rows',
        autoClose: 5000,
      });
      return;
    }

    if (!createdAvatarId) {
      toast.error('Invalid avatar ID. Please try creating the avatar again.', {
        toastId: 'invalid-avatar-id'
      });
      return;
    }

    if (selectedRows.length === 0) {
      toast.error('Please select at least one email to send invitations.', {
        toastId: 'no-emails-selected'
      });
      return;
    }

    if (allowedRows.length === 0) {
      toast.error(
        'Please select at least one valid recipient (from the list or add someone with "+ Add someone").',
        { toastId: 'invite-no-allowed-emails', autoClose: 5000 }
      );
      return;
    }

    // Validate selected rows have valid email addresses (only allowed rows are sent)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Validate avatar ID is a number
    const avatarIdNum = Number(createdAvatarId);
    if (!avatarIdNum || isNaN(avatarIdNum)) {
      toast.error('Invalid avatar ID. Please try creating the avatar again.', {
        toastId: 'invalid-avatar-id'
      });
      return;
    }

    // Open blank popup synchronously — must be inside user gesture, before any await
    const hasGiftRowsEarly =
      !isBetaInviter && allowedRows.some((r) => !r.disabled && isInviteGiftAction(r.action));
    let giftPopupEarly = null;
    if (hasGiftRowsEarly) {
      const popupWidth = 500;
      const popupHeight = 700;
      const left = Math.round(window.screenX + (window.outerWidth - popupWidth) / 2);
      const top = Math.round(window.screenY + (window.outerHeight - popupHeight) / 2);
      giftPopupEarly = window.open(
        'about:blank',
        'PayPalGift',
        `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes,resizable=yes`
      );
    }

    setSendingInvites(true);

    let selectedEmailRecipients = [];

    try {
      const enabledRows = allowedRows.filter((r) => !r.disabled);

      const needsGiftPlanDetails = enabledRows.some((r) => (r.action || 'send_invite') !== 'send_invite');
      let planData = avatarPlanDetails;
      if (needsGiftPlanDetails) {
        try {
          const fetched = await fetchAvatarPlan();
          if (fetched) planData = fetched;
        } catch (e) {
          console.warn('Invite: failed to prefetch avatar plan details:', e);
        }
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidSelected = enabledRows.filter((r) => !emailRegex.test((r.email || '').trim()));
      if (invalidSelected.length > 0) {
        toast.error('Please fix invalid/empty email addresses before sending invites.', {
          toastId: 'invite-invalid-emails',
          autoClose: 4000,
        });
        return;
      }

      const sendInviteRows = isBetaInviter
        ? enabledRows
        : enabledRows.filter((r) => (r.action || 'send_invite') === 'send_invite');
      const giftRows = isBetaInviter
        ? []
        : enabledRows.filter((r) => isInviteGiftAction(r.action));

      const dropdownOpts = getDropdownOptions(planData);

      if (sendInviteRows.length === 0 && giftRows.length === 0) {
        toast.error('No recipients to process.', { toastId: 'invite-none' });
        return;
      }

      if (sendInviteRows.length > 0) {
        selectedEmailRecipients = buildSelectedEmailRecipients(sendInviteRows, planData);

        const missingPlanPricing = selectedEmailRecipients.filter(
          (r) =>
            Number(r.plan_id) !== 1 &&
            (r.plan_pricing_id === undefined || r.plan_pricing_id === null || isNaN(r.plan_pricing_id))
        );
        if (missingPlanPricing.length > 0) {
          toast.error(
            'Unable to send some invites because plan details are missing. Open the action dropdown once to load plan options.',
            { toastId: 'invite-missing-plan-pricing', autoClose: 6000 }
          );
          return;
        }

        const recipientsWithoutPlanId = selectedEmailRecipients.filter(
          (r) => r.plan_id === undefined || r.plan_id === null || isNaN(r.plan_id)
        );
        if (recipientsWithoutPlanId.length > 0) {
          toast.error(
            `${recipientsWithoutPlanId.length} recipient(s) are missing plan_id. Please try again.`,
            { toastId: 'missing-plan-id-error', autoClose: 5000 }
          );
          return;
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('Invite-emails payload (send-invite rows only):', selectedEmailRecipients);
        }

        const response = await avatarsAPI.sendInviteEmails(createdAvatarId, {
          selected_email_recipients: selectedEmailRecipients,
        });
        const isSuccess =
          response.status === true || response.status_code === 200 || response.status_code === 201;
        if (!isSuccess) throw new Error('Failed to send invitations');

        const sentIds = new Set(sendInviteRows.map((r) => r.id));
        setInviteRows((prev) =>
          prev.map((r) => (sentIds.has(r.id) ? { ...r, disabled: true } : r))
        );

        sessionStorage.removeItem('avatars_fetched');
        sessionStorage.removeItem('avatars_data');
        sessionStorage.removeItem('has_avatars');

        const currentUserId = userId || (await getUserId());
        if (currentUserId) {
          try {
            await projectsAPI.getInviteeMembers(currentUserId);
          } catch (inviteeError) {
            console.warn('Failed to fetch invitee members after sending invites:', inviteeError);
          }
        }
      }

      // No gift rows — all send_invite rows processed. Close modal and go to chat.
      if (giftRows.length === 0) {
        handleSkipInvites();
        return;
      }

      if (giftRows.length > 0) {
        const pricingOptions = getPricingOptionsFromPlan(planData);

        const resolveGiftRowForPaypal = (row) => {
          const action = row.action || 'send_invite';
          const sel = dropdownOpts.find((o) => o.value === action);
          if (!sel || !isInviteGiftAction(action)) return null;
          let plan_id = sel.plan_id;
          let plan_pricing_id =
            sel.plan_pricing_id !== undefined && sel.plan_pricing_id !== null
              ? sel.plan_pricing_id
              : null;
          const pricingN = Number(plan_pricing_id);
          if (!Number.isFinite(pricingN)) return null;
          const fromApi = pricingOptions.find((o) => invitePlanPricingIdFromOpt(o) === pricingN);
          let resolvedPlanId = Number(plan_id);
          if (!Number.isFinite(resolvedPlanId) && fromApi != null) {
            resolvedPlanId = Number(fromApi.plan_id);
          }
          if (!Number.isFinite(resolvedPlanId)) {
            resolvedPlanId = Number(sel.plan_id);
          }
          if (!Number.isFinite(resolvedPlanId)) {
            resolvedPlanId = 1;
          }
          plan_id = resolvedPlanId;
          let interval = sel.interval || null;
          if (!interval) {
            const po = pricingOptions.find((o) => invitePlanPricingIdFromOpt(o) === pricingN);
            interval = po ? classifyInvitePlanInterval(po) : null;
          }
          return {
            email: (row.email || '').trim(),
            action,
            plan_id,
            plan_pricing_id: pricingN,
            plan_interval: interval || 'monthly',
          };
        };

        const resolvedGifts = giftRows.map(resolveGiftRowForPaypal).filter(Boolean);
        if (resolvedGifts.length !== giftRows.length) {
          toast.error(
            'One or more gift rows have an invalid plan. Open the action menu on each gift row to refresh options.',
            { toastId: 'invite-gift-invalid-plan', autoClose: 6000 }
          );
          return;
        }

        const giftByPid = new Map();
        resolvedGifts.forEach((g) => {
          const pid = Number(g.plan_pricing_id);
          if (!Number.isFinite(pid)) return;
          if (!giftByPid.has(pid)) giftByPid.set(pid, []);
          giftByPid.get(pid).push(g);
        });
        const giftInviteParts = [...giftByPid.entries()].map(([ppid, rows]) => ({
          invite_emails: rows.map((r) => r.email),
          plan_id: rows[0]?.plan_id ?? 1,
          plan_pricing_id: Number(ppid),
          i_will_pay: 1,
        }));

        const primaryGiftRow = resolvedGifts[0];
        const planPricingId = primaryGiftRow.plan_pricing_id;
        const planInterval = primaryGiftRow.plan_interval || 'monthly';

        const fromPlan = buildInviteGiftPaypalFromPlan(planData, resolvedGifts, dropdownOpts);
        let { gift_line_items, combinedGiftUsd } = fromPlan;
        const fallbackSum = Math.round(sumGiftInvitePrices(resolvedGifts, dropdownOpts) * 100) / 100;
        if (!Number.isFinite(combinedGiftUsd) || combinedGiftUsd <= 0) {
          combinedGiftUsd = fallbackSum;
        }

        const paypalQuantity = combinedGiftUsd > 0 ? 1 : resolvedGifts.length;

        if (!Number.isFinite(combinedGiftUsd) || combinedGiftUsd <= 0) {
          toast.error(
            'Could not determine gift total from plan prices. Open each row’s action menu to load plans, then try again.',
            { toastId: 'invite-gift-no-total', autoClose: 6000 }
          );
          return;
        }

        if (inviteModalPaypalCleanupRef.current) inviteModalPaypalCleanupRef.current();

        const pendingGiftPayload = { gift_invite_parts: giftInviteParts };

        const prelimCtx = {
          order_id: null,
          avatar_plan_id: planPricingId,
          plan_interval: planInterval,
          auto_renew: false,
          quantity: paypalQuantity,
          total_usd: combinedGiftUsd,
          bundle_total_usd: combinedGiftUsd,
          gift_line_items: gift_line_items.length > 0 ? gift_line_items : undefined,
          started_from: '/create-avatar',
          context: 'popup',
          invite_modal_checkout: true,
          pending_gift_invite_bulk: pendingGiftPayload,
        };
        try {
          sessionStorage.setItem('paypal_checkout_context', JSON.stringify(prelimCtx));
          localStorage.setItem('paypal_checkout_context', JSON.stringify(prelimCtx));
        } catch (_) { /* ignore */ }

        const createOrderRes = await paymentsAPI.createPaypalOrder({
          avatar_plan_id: planPricingId,
          plan_interval: planInterval,
          quantity: paypalQuantity,
          total_usd: combinedGiftUsd,
          gift_line_items: gift_line_items.length > 0 ? gift_line_items : undefined,
        });

        let approveUrl =
          createOrderRes?.data?.approve_url ||
          createOrderRes?.approve_url ||
          null;
        if (!approveUrl && createOrderRes?.data?.links && Array.isArray(createOrderRes.data.links)) {
          const link = createOrderRes.data.links.find((l) => l.rel === 'approve');
          if (link) approveUrl = link.href;
        }

        if (!approveUrl) {
          toast.error('Could not start PayPal checkout. Please try again.');
          return;
        }

        const orderId =
          createOrderRes?.data?.order_id ||
          createOrderRes?.order_id ||
          (() => {
            try {
              return new URL(approveUrl).searchParams.get('token');
            } catch (_) {
              return null;
            }
          })();

        const ctx = {
          ...prelimCtx,
          order_id: orderId,
        };
        try {
          sessionStorage.setItem('paypal_checkout_context', JSON.stringify(ctx));
          localStorage.setItem('paypal_checkout_context', JSON.stringify(ctx));
        } catch (_) { /* ignore */ }

        try {
          sessionStorage.setItem(
            INVITE_MODAL_GIFT_EMAILS_KEY,
            JSON.stringify(giftRows.map((r) => (r.email || '').trim().toLowerCase()))
          );
        } catch (_) { /* ignore */ }

        try {
          localStorage.setItem(
            `paypal_post_capture_invite_${orderId}`,
            JSON.stringify(pendingGiftPayload)
          );
        } catch (_) { /* ignore */ }

        // Navigate the already-open blank popup to the real PayPal URL (or fall back to full-page)
        const giftPopupBlocked = !giftPopupEarly || giftPopupEarly.closed || typeof giftPopupEarly.closed === 'undefined';
        if (giftPopupBlocked) {
          const fullPageCtx = { ...ctx, context: 'full-page' };
          try {
            sessionStorage.setItem('paypal_checkout_context', JSON.stringify(fullPageCtx));
            localStorage.setItem('paypal_checkout_context', JSON.stringify(fullPageCtx));
          } catch (_) { /* ignore */ }
          window.location.href = approveUrl;
          return;
        }

        giftPopupEarly.location.href = approveUrl;

        const messageHandler = (event) => {
          if (event.origin !== window.location.origin) return;
          if (event.data?.type === 'PAYPAL_SUCCESS' || event.data?.type === 'PAYPAL_ERROR') {
            if (inviteModalPaypalCleanupRef.current) inviteModalPaypalCleanupRef.current();
          }
        };
        const pollTimer = setInterval(() => {
          if (giftPopupEarly.closed) {
            try { sessionStorage.removeItem(INVITE_MODAL_GIFT_EMAILS_KEY); } catch (_) { /* ignore */ }
            if (inviteModalPaypalCleanupRef.current) inviteModalPaypalCleanupRef.current();
          }
        }, 600);
        inviteModalPaypalCleanupRef.current = () => {
          clearInterval(pollTimer);
          window.removeEventListener('message', messageHandler);
          inviteModalPaypalCleanupRef.current = null;
        };
        window.addEventListener('message', messageHandler);
        return;
      }
    } catch (error) {
      console.error('Error sending invite emails:', error);
      console.error('Error details:', {
        status: error.status || error.statusCode,
        message: error.message,
        response: error.response,
        request: error.request
      });

      let errorMessage = error.message || 'Failed to send invitations. Please try again.';

      // Extract detailed error from backend response
      if (error.response) {
        // Try multiple paths for error message
        const backendError =
          error.response.detail ||
          error.response.error?.msg ||
          error.response.error?.error ||
          error.response.error ||
          error.response.message ||
          error.response.msg;

        if (backendError) {
          if (Array.isArray(backendError)) {
            // Handle validation errors array (FastAPI format)
            errorMessage = backendError.map(err =>
              err.msg || err.message || JSON.stringify(err)
            ).join(', ');
          } else if (typeof backendError === 'string') {
            errorMessage = backendError;
          } else if (typeof backendError === 'object') {
            errorMessage = JSON.stringify(backendError);
          }
        }

        // Log full response for debugging
        console.error('Full backend error response:', JSON.stringify(error.response, null, 2));
      }

      if (
        selectedEmailRecipients.length > 0 &&
        (errorMessage.toLowerCase().includes('plan_id') ||
          errorMessage.toLowerCase().includes('invalid plan') ||
          errorMessage.toLowerCase().includes('inactive'))
      ) {
        const uniquePlanIds = [...new Set(selectedEmailRecipients.map((r) => r.plan_id))];
        console.error('Plan ID error. Payload:', JSON.stringify(selectedEmailRecipients, null, 2), 'Plan IDs:', uniquePlanIds);
        if (errorMessage.toLowerCase().includes('inactive') || errorMessage.toLowerCase().includes('invalid')) {
          errorMessage = `Invalid plan_id: The plan IDs (${uniquePlanIds.join(', ')}) do not exist or are inactive. Please ensure the avatar plan API returns valid plans.`;
        }
      }

      if (error.status === 401 || error.statusCode === 401) {
        // Handle 401 Unauthorized - token expired or invalid
        const errorDetail = error.response?.error?.message || error.response?.error?.code || error.message;
        if (errorDetail && (errorDetail.toLowerCase().includes('token') || errorDetail.toLowerCase().includes('unauthorized'))) {
          // Clear invalid tokens
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          sessionStorage.clear();
          errorMessage = 'Your session has expired. Please sign in again.';
          setTimeout(() => {
            navigate('/signin');
          }, 2000);
        } else {
          errorMessage = errorDetail || 'Unauthorized. Please sign in again.';
          setTimeout(() => {
            navigate('/signin');
          }, 2000);
        }
      } else if (error.status === 404 || error.statusCode === 404) {
        errorMessage = 'Avatar not found. Please try creating the avatar again.';
      } else if (error.status >= 500 || error.statusCode >= 500) {
        // Show backend error details if available
        const serverError = error.response?.detail || error.response?.error || error.response?.message;
        if (serverError) {
          errorMessage = `Server error: ${typeof serverError === 'string' ? serverError : JSON.stringify(serverError)}`;
        } else {
          errorMessage = `Server error (${error.status || error.statusCode}). Please check the browser console for details.`;
        }
      }

      toast.error(errorMessage, {
        toastId: (error.status === 401 || error.statusCode === 401) ? 'session-expired' : 'invites-send-error',
        autoClose: 6000
      });
    } finally {
      setSendingInvites(false);
    }
  };

  // Skip sending invites and go to chat
  const handleSkipInvites = () => {
    // Clear avatars cache
    sessionStorage.removeItem('avatars_fetched');
    sessionStorage.removeItem('avatars_data');
    sessionStorage.removeItem('has_avatars');

    // Close modal and redirect
    setShowInviteModal(false);
    setInviteRows([]);
    setCreatedAvatarId(null);

    navigate('/chat', { replace: true });
  };

  // Note: Email sending is now handled automatically by the backend when creating avatar
  // The selected_email_recipients are sent as part of the avatar creation request

  // Check if a file is a media file (image, video, or audio) that should be filtered out
  // Note: Archive files (zip, rar, 7z) are NOT filtered out
  const isMediaFile = (file) => {
    if (!file || !file.name) return false;

    const ext = file.name.split('.').pop().toLowerCase();
    const mimeType = file.type ? file.type.toLowerCase() : '';

    // Archive extensions - DO NOT filter these out
    const archiveExts = ['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz'];
    if (archiveExts.includes(ext)) {
      return false; // Keep archive files
    }

    // Image extensions
    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico', 'tiff', 'tif', 'heic', 'heif', 'raw', 'cr2', 'nef', 'orf', 'sr2', 'psd', 'ai', 'eps'];
    // Video extensions
    const videoExts = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'm4v', 'mpg', 'mpeg', '3gp', 'ogv', 'ts', 'mts', 'm2ts'];
    // Audio extensions
    const audioExts = ['mp3', 'wav', 'flac', 'aac', 'ogg', 'wma', 'm4a', 'opus', 'amr', 'aiff', 'au', 'ra', 'rm', 'wv', 'ape', 'dsd'];

    // Check by extension
    if (imageExts.includes(ext) || videoExts.includes(ext) || audioExts.includes(ext)) {
      return true;
    }

    // Check by MIME type
    if (mimeType) {
      if (mimeType.startsWith('image/') || mimeType.startsWith('video/') || mimeType.startsWith('audio/')) {
        return true;
      }
    }

    return false;
  };

  // Filter out media files and return only text-based files
  const filterTextFiles = (files) => {
    if (!files || files.length === 0) return [];

    const textFiles = [];
    const filteredOut = [];

    files.forEach(file => {
      if (isMediaFile(file)) {
        filteredOut.push(file);
      } else {
        textFiles.push(file);
      }
    });

    // Removed toast notification for filtered media files - silent filtering

    return textFiles;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getFileType = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    const nameLower = filename.toLowerCase();

    // Check for email-related archives (gmail, email, mail)
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
      if (nameLower.includes('gmail') || nameLower.includes('email') || nameLower.includes('mail')) {
        return 'email-archive';
      }
      // Check for message-related archives (imessage, message, chat, sms, whatsapp)
      if (nameLower.includes('imessage') || nameLower.includes('message') || nameLower.includes('chat') || nameLower.includes('sms') || nameLower.includes('whatsapp')) {
        return 'message-archive';
      }
      return 'archive';
    }
    if (['pdf', 'doc', 'docx', 'txt'].includes(ext)) return 'document';
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return 'image';
    return 'file';
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'email-archive':
        // Envelope icon for email archives
        return (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case 'message-archive':
        // Speech bubble icon for message archives
        return (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        );
      case 'archive':
        return (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
          </svg>
        );
      case 'document':
        return (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'image':
        return (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      default:
        return (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
    }
  };

  // Get user ID - use cached value or fetch if not available
  const getUserId = async () => {
    // Return cached user ID if available
    if (userId) {
      console.log('CreateAvatarPage: Using cached userId:', userId);
      return userId;
    }

    // Check sessionStorage first (might be set by Navbar or LandingPage)
    const userFetchedInSession = sessionStorage.getItem('user_fetched');
    const storedUserId = sessionStorage.getItem('user_id');

    if (userFetchedInSession && storedUserId) {
      const cachedUserId = parseInt(storedUserId, 10);
      setUserId(cachedUserId); // Update local state
      console.log('CreateAvatarPage: Using userId from sessionStorage:', cachedUserId);
      return cachedUserId;
    }

    // If not cached and not already fetching, fetch it once
    if (!userFetchedRef.current && !userFetchedInSession) {
      await fetchUserOnce();
      return userId;
    }

    // If currently fetching or already fetched in session, return null or wait
    // The fetchUserOnce function will handle the sessionStorage check
    return null;
  };

  // Check if current plan requires payment
  const requiresPayment = () => {
    const current = getCurrentSelectedPlan();
    if (!current) return false;
    return Number(current.price_usd || 0) > 0;
  };

  const hasRestoredFilePlaceholdersOnly = () => {
    return (
      Array.isArray(uploadedFiles) &&
      uploadedFiles.length > 0 &&
      (!actualFiles || actualFiles.length === 0) &&
      uploadedFiles.some((f) => Boolean(f?.isRestored))
    );
  };

  useEffect(() => {
    if (hasRestoredFilePlaceholdersOnly()) {
      setUploadedFiles([]);
      setActualFiles([]);
    }
  }, [uploadedFiles, actualFiles]);

  const clearPaymentDraft = () => {
    const currentUid = getCurrentSessionUserId();
    if (!currentUid) return;
    sessionStorage.removeItem(getPaymentDraftKey(currentUid));
  };

  const persistActivatedPlanForCurrentUser = (activatedPlan) => {
    try {
      const currentUid = getCurrentSessionUserId();
      if (!currentUid || !activatedPlan?.avatar_plan_id || !activatedPlan?.plan_interval) return;
      sessionStorage.setItem(getActivatedPlanKey(currentUid), JSON.stringify(activatedPlan));
    } catch (err) {
      console.warn('Failed to persist activated plan:', err);
    }
  };

  const savePaymentDraft = () => {
    try {
      const currentUid = getCurrentSessionUserId();
      if (!currentUid) return;
      const draft = {
        user_id: currentUid,
        firstName: (getValues('firstName') || '').trim(),
        lastName: (getValues('lastName') || '').trim(),
        avatarType,
        selectedRelation,
        selectedPlanId,
        billingPeriod,
        autoRenew: Boolean(autoRenew),
        uploadedFiles: (uploadedFiles || []).map((file, idx) => ({
          id: file?.id ?? `file-${idx}`,
          name: file?.name || '',
          size: Number(file?.size || 0),
          type: file?.type || '',
        })),
        savedAt: new Date().toISOString(),
      };
      sessionStorage.setItem(getPaymentDraftKey(currentUid), JSON.stringify(draft));
    } catch (err) {
      console.warn('Failed to save create-avatar payment draft:', err);
    }
  };

  // Process payment using backend PayPal order flow (create-order -> PayPal approve -> /payment/success capture)
  const processPayment = async (planOverride = null, intervalOverride = null) => {
    if (orderCreatingRef.current) return; // Bug 1: guard against duplicate order creation
    orderCreatingRef.current = true;
    const current = planOverride ? normalizeSelectedPlan(planOverride, intervalOverride) : getCurrentSelectedPlan();
    if (!current || !current.avatar_plan_id) {
      toast.error('Please select a valid plan first.');
      orderCreatingRef.current = false;
      return;
    }

    // Free trial / zero-price plan: no PayPal redirect required.
    if (Number(current.price_usd || 0) <= 0) {
      setProcessingPayment(true);
      try {
        const captureRes = await paymentsAPI.capturePaypalOrder({
          order_id: `FREE-${Date.now()}`,
          avatar_plan_id: current.avatar_plan_id,
          plan_interval: current.interval,
          auto_renew: Boolean(autoRenew),
        });
        const payload = captureRes?.data || captureRes || {};
        const activated = {
          avatar_plan_id: current.avatar_plan_id,
          plan_interval: current.interval,
          auto_renew: Boolean(autoRenew),
          subscription_id: payload?.subscription_id || null,
          invoice_id: payload?.invoice_id || null,
          invoice_download_url: payload?.invoice_download_url || null,
        };
        setPaymentActivatedPlan(activated);
        persistActivatedPlanForCurrentUser(activated);
        sessionStorage.setItem('paypal_capture_result', JSON.stringify(activated));
        setShowPaymentModal(false);
      } catch (error) {
        console.error('Free-plan activation failed:', error);
        toast.error(error?.message || 'Unable to activate free plan. Please try again.');
      } finally {
        setProcessingPayment(false);
      }
      return;
    }

    setProcessingPayment(true);
    try {
      // Persist draft before redirecting to PayPal.
      savePaymentDraft();

      const createOrderRes = await paymentsAPI.createPaypalOrder({
        avatar_plan_id: current.avatar_plan_id,
        plan_interval: current.interval,
      });

      const orderId = createOrderRes?.data?.order_id || createOrderRes?.order_id || null;
      const approveUrl = createOrderRes?.data?.approve_url || createOrderRes?.approve_url || null;
      if (!orderId || !approveUrl) {
        throw new Error('Invalid create-order response. Missing order_id or approve_url.');
      }

      // Persist checkout context for PaymentSuccess capture and optional restore.
      // Invited users (signup_invite_params with avatar_id) should land on relation-capture after payment.
      let _startedFrom = '/create-avatar';
      try {
        const _inviteRaw = localStorage.getItem('signup_invite_params');
        if (_inviteRaw) {
          const _inviteParsed = JSON.parse(_inviteRaw);
          if (_inviteParsed?.invite_id && _inviteParsed?.avatar_id) {
            _startedFrom = `/relation-capture?avatar_id=${_inviteParsed.avatar_id}`;
          }
        }
      } catch (_) { }
      localStorage.setItem('paypal_checkout_context', JSON.stringify({
        order_id: orderId,
        avatar_plan_id: current.avatar_plan_id,
        plan_interval: current.interval,
        auto_renew: Boolean(autoRenew),
        expected_amount_usd: createOrderRes?.data?.expected_amount_usd ?? createOrderRes?.expected_amount_usd ?? null,
        started_from: _startedFrom,
        created_at: new Date().toISOString(),
      }));

      // Redirect to PayPal (same window)
      setShowPlanModal(false);
      window.location.href = approveUrl;

    } catch (error) {
      console.error('Payment processing error:', error);
      toast.error(error?.message || 'Payment processing failed. Please try again.', {
        toastId: 'payment-error'
      });
    } finally {
      setProcessingPayment(false);
      orderCreatingRef.current = false;
    }
  };

  // Create avatar after successful payment
  const createAvatarAfterPayment = async (avatarData) => {
    setLoading(true);

    try {
      const response = await avatarsAPI.createAvatar(
        avatarData.avatarData,
        avatarData.actualFiles,
        avatarData.selectedEmailAddresses
      );

      // Handle success
      const isSuccess = (response.status === true || response.status_code === 201 || response.status_code === 200) && response.data;

      if (isSuccess) {
        clearPaymentDraft();
        const newAvatar = response.data;

        // Call avatar plan API after avatar creation
        try {
          const planResponse = await avatarsAPI.getAvatarPlan();
          if (planResponse.status && planResponse.data) {
            setAvatarPlanDetails(planResponse.data);
            console.log('Avatar plan details fetched after avatar creation:', planResponse.data);

            // Update existing inviteRows with plan_pricing_id if they don't have it
            // Use planResponse.data directly instead of getDropdownOptions() to avoid closure issues
            if (inviteRows.length > 0 && planResponse.data) {
              const planDataForOpts = planResponse.data;
              const opts = getDropdownOptions(planDataForOpts);
              setInviteRows((prev) =>
                prev.map((row) => {
                  if (row.plan_pricing_id) return row;
                  const action = row.action || 'send_invite';
                  const sel = opts.find((o) => o.value === action);
                  if (sel?.plan_pricing_id != null) {
                    const pid = invitePlanPricingIdFromOpt({
                      plan_pricing_id: sel.plan_pricing_id,
                    });
                    if (pid != null) {
                      return {
                        ...row,
                        plan_pricing_id: pid,
                        plan_id: sel.plan_id ?? row.plan_id ?? 1,
                        ...(sel.price_usd != null && Number.isFinite(Number(sel.price_usd))
                          ? { price_usd: Number(sel.price_usd) }
                          : {}),
                      };
                    }
                  }
                  return row;
                })
              );
            }
          }
        } catch (error) {
          console.error('Error fetching avatar plan after avatar creation:', error);
        }


        // Open invite modal with one blank row for manual entry
        setCreatedAvatarId(newAvatar.id);
        setInviteAvatarName(newAvatar.first_name || '');
        setInviteRows([{ id: Date.now() + Math.random(), email: '', name: '', note: null, selected: true, isManual: true, action: 'send_invite', plan_id: 1, plan_pricing_id: null }]);
        setShowInviteModal(true);
        setLoading(false);
        setPendingAvatarData(null);
        return;

        // Clear avatars and user cache
        sessionStorage.removeItem('avatars_fetched');
        sessionStorage.removeItem('avatars_data');
        sessionStorage.removeItem('has_avatars');
        sessionStorage.removeItem('user_fetched');
        sessionStorage.removeItem('user_data');

        // Call invitee members API after avatar creation
        const currentUserId = userId || await getUserId();
        if (currentUserId) {
          try {
            await projectsAPI.getInviteeMembers(currentUserId);
            console.log('Invitee members fetched after avatar creation (after payment)');
          } catch (inviteeError) {
            console.warn('Failed to fetch invitee members after avatar creation:', inviteeError);
            // Don't block navigation if this fails
          }
        }

        // Redirect to chat page
        setTimeout(() => {
          navigate('/chat', { replace: true });
        }, 2000);
      } else {
        throw new Error('Avatar creation failed after payment');
      }
    } catch (error) {
      console.error('Avatar creation error after payment:', error);
      toast.error('Failed to create avatar after payment. Please contact support.', {
        toastId: 'avatar-creation-error-after-payment'
      });
    } finally {
      setLoading(false);
      setPendingAvatarData(null);
    }
  };

  const handleCreationSuccess = (response) => {
    clearPaymentDraft();
    setPaymentActivatedPlan(null);
    try {
      sessionStorage.removeItem('paypal_capture_result');
      const _uid = getCurrentSessionUserId();
      if (_uid) sessionStorage.removeItem(getActivatedPlanKey(_uid));
    } catch (_) { };
    const newAvatar = response.data || response;

    setCreatedAvatarId(newAvatar.id);
    setInviteAvatarName(newAvatar.first_name || '');
    setInviteRows([{ id: Date.now() + Math.random(), email: '', name: '', note: null, selected: true, isManual: true, action: 'send_invite', plan_id: 1, plan_pricing_id: null }]);
    setShowInviteModal(true);
  };

  const onSubmit = async (data) => {
    setDuplicateNameError('');
    // Check if a free plan is selected (dynamic: based on plan price from API)
    const isFreePlanSelected = (billingPeriod === 'free') || (selectedPlan && selectedPlan.price_usd === 0);

    // Check if user came via invite link (signup_invite_params has invite_id)
    const hasSignupInviteId = (() => {
      try {
        const raw = localStorage.getItem('signup_invite_params');
        if (!raw) return false;
        const parsed = JSON.parse(raw);
        return !!parsed?.invite_id;
      } catch (_) { return false; }
    })();

    // Validate that files are uploaded
    if (!actualFiles || actualFiles.length === 0) {
      if (hasRestoredFilePlaceholdersOnly()) {
        toast.error('Please re-upload your files. Previous upload list is shown for reference only.');
      } else {
        toast.error('Please upload at least one file to create an avatar.');
      }
      setLoading(false);
      return;
    }
    console.log('CreateAvatarPage: Submitting with files:', {
      count: actualFiles.length,
      files: actualFiles.map(f => ({ name: f.name, size: f.size, type: f.type }))
    });

    // Validate total file size before submission - using storage limit from plan
    const maxStorageGB = Number(selectedPlan?.storage || 5);
    const MAX_TOTAL_SIZE = maxStorageGB * 1024 * 1024 * 1024; // in bytes
    const totalSize = actualFiles.reduce((total, file) => total + file.size, 0);

    if (totalSize > MAX_TOTAL_SIZE) {
      const totalGB = (totalSize / (1024 * 1024 * 1024)).toFixed(2);
      toast.error(`Total file size (${totalGB} GB) exceeds the ${maxStorageGB} GB limit. Please remove some files.`, {
        toastId: 'file-size-limit-submit',
        autoClose: 5000
      });
      return;
    }

    // Get user ID - use cached value if available
    const currentUserId = userId || await getUserId();
    if (!currentUserId) {
      toast.error('Unable to get user information. Please sign in again.');
      navigate('/signin');
      return;
    }

    // Check avatar limit against max_avatars
    try {
      setLoading(true);
      const avatarsResponse = await avatarsAPI.getAvatars(currentUserId);
      const currentAvatars = avatarsResponse.data?.avatars || avatarsResponse.data || [];

      // Check avatar limit against max_avatars — skip if creating a paid "Loved One" avatar 
      // or if current subscription is cancelled/expired (allow starting a new one).
      let subscription = userData?.subscription;
      let isSubscriptionActive = hasActiveSubscription(userData);
      if (!isSubscriptionActive) {
        const apiSub = await fetchActiveSubscriptionFromApi(localStorage.getItem('access_token'));
        if (apiSub) {
          subscription = apiSub;
          isSubscriptionActive = hasActiveSubscription(apiSub);
          if (isSubscriptionActive) {
            setUserData((prev) => ({
              ...(prev || {}),
              subscription: apiSub,
            }));
          }
        }
      }

      const userPlan = subscription?.plan || userData?.plan;
      const currentAvatarsCount = currentAvatars.length;
      const maxAvatars = userPlan?.max_avatars;

      if (!isSubscriptionActive) {
        toast.error('An active subscription is required to create an avatar. Please subscribe to get started.', {
          toastId: 'no-subscription-submit',
          autoClose: 6000
        });
        setLoading(false);
        return;
      }
      if (typeof maxAvatars === 'number' && currentAvatarsCount >= maxAvatars) {
        toast.error(`You have reached the maximum number of avatars (${maxAvatars}) for your plan. Please upgrade to create more.`, {
          toastId: 'max-avatars-submit-limited',
          autoClose: 6000
        });
        setLoading(false);
        return;
      }
    } catch (err) {
      console.warn('Failed to check avatar limits:', err);
    }

    // Scroll to top immediately when Create Avatar is clicked
    window.scrollTo({ top: 0, behavior: 'auto' });
    // Also scroll document element to ensure we reach absolute top
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
    }

    proceedWithAvatarCreation(data);
  };

  const proceedWithAvatarCreation = async (formData) => {
    // Use provided form data
    const data = formData;
    if (!data) {
      toast.error('Form data is missing. Please try again.');
      return;
    }

    // Get user ID - use cached value if available
    const currentUserId = userId || await getUserId();
    if (!currentUserId) {
      toast.error('Unable to get user information. Please sign in again.');
      navigate('/signin');
      return;
    }

    // Get avatar_type_id and plan_id dynamically from /api/plans/avatar-plans API response
    const selectedAvatarType = avatarPlans.find(avatarTypeItem =>
      normalizeAvatarTypeName(avatarTypeItem.name) === normalizeAvatarTypeName(avatarType)
    );

    // plan_id from API (selected plan's id, or first plan of selected avatar type)
    const resolvedPlanId = selectedPlanId ?? selectedAvatarType?.plans?.[0]?.id ?? avatarPlans[0]?.plans?.[0]?.id ?? null;

    // Get selected email addresses (empty array - no emails from documents)
    const selectedEmailAddresses = [];

    const avatarData = {
      first_name: data.firstName,
      last_name: data.lastName,
      avatar_type: avatarType || '',
      user_id: currentUserId,
      plan_id: Number(resolvedPlanId),
      relation: selectedRelation || null,
    };

    // Check if free plan selected (dynamic: based on selected plan price, not avatar type name)
    const isFreePlanSelected = (billingPeriod === 'free') || (selectedPlan && selectedPlan.price_usd === 0);

    // Check if user came via invite link
    const hasSignupInviteIdInPayload = (() => {
      try {
        const raw = localStorage.getItem('signup_invite_params');
        if (!raw) return false;
        const parsed = JSON.parse(raw);
        return !!parsed?.invite_id;
      } catch (_) { return false; }
    })();

    // Add plan_interval when selected plan has monthly/yearly interval (dynamic from API)
    const planInterval = selectedPlan?.interval || billingPeriod;
    if (planInterval === 'monthly' || planInterval === 'yearly') {
      avatarData.plan_interval = planInterval;
    }

    // Add invite_id from signup_invite_params (user came via invite link)
    try {
      const raw = localStorage.getItem('signup_invite_params');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.invite_id) {
          avatarData.invite_id = parsed.invite_id;
        }
      }
    } catch (_) { /* ignore */ }

    // For free plans, create avatar directly
    setLoading(true);

    let backgroundJobStarted = false;
    try {
      // Log the final avatarData before sending
      console.log('CreateAvatarPage: Final avatarData before API call:', avatarData);

      // Send avatar data, files, and selected email recipients to API
      const response = await avatarsAPI.createAvatar(avatarData, actualFiles, selectedEmailAddresses);

      // Handle API response structure: { status: true, status_code: 201/202, data: {...} }
      // 202 = background job accepted; 201/200 = legacy synchronous success
      const isSuccess = (response.status === true || response.status_code === 202 || response.status_code === 201 || response.status_code === 200) && response.data;

      if (isSuccess) {
        // New SSE-based flow: server returns job_id immediately, processes in background
        if (response.data?.job_id) {
          const firstName = data?.firstName || '';
          setProgressMessage(`Reading ${firstName}'s files…`);
          setProgressStage('reading');
          setJobId(response.data.job_id);
          // setLoading stays true — polling handler will clear it
          backgroundJobStarted = true;
          return;
        }
        // Legacy synchronous response (fallback)
        handleCreationSuccess(response);
      } else {
        // If response doesn't match expected structure, show error
        console.error('Unexpected response structure:', response);
        toast.error('Avatar creation response was not in the expected format. Please try again.', {
          toastId: 'avatar-creation-unexpected-response'
        });
      }
    } catch (error) {
      console.error('Avatar creation error:', error);
      console.error('Error details:', {
        status: error.status || error.statusCode,
        message: error.message,
        response: error.response,
        errorName: error.name
      });

      // Show more specific error messages based on error type
      let errorMessage = error.message || 'Failed to create avatar. Please try again.';

      // Check for nested error structure in response (API returns { error: { msg: "...", error: "..." } })
      if (error.response && error.response.error) {
        const nestedError = error.response.error;
        errorMessage = nestedError.msg || nestedError.error || nestedError.message || errorMessage;
      }

      // For network errors (no status code), preserve the detailed error message from API service
      if (!error.status && !error.statusCode) {
        // Network error - use the detailed message from api.js
        errorMessage = error.message || 'Network error: Unable to connect to the API server. Please check your connection and try again.';
      } else if (error.status >= 500 || error.statusCode >= 500) {
        // Server error - use the message from API response if available, otherwise generic message
        errorMessage = error.message || `Server error (${error.status || error.statusCode}) occurred while creating avatar. Please try again in a few moments. If the problem persists, contact support.`;
      } else if (error.status === 409 || error.statusCode === 409) {
        // Duplicate avatar name — show inline near name fields, not as a toast
        setDuplicateNameError(error.message || 'It looks like you already have an Avatar with that name in your family group. Each Avatar needs a unique name.');
        setLoading(false);
        return;
      } else if (error.status === 400 || error.statusCode === 400) {
        // Bad request - use the message from API response (may include nested error)
        errorMessage = error.message || 'Invalid data provided. Please check your input and try again.';
      } else if (error.status === 401 || error.statusCode === 401) {
        // Unauthorized
        errorMessage = error.message || 'Your session has expired. Please sign in again.';
        setTimeout(() => {
          navigate('/signin');
        }, 2000);
      } else if (error.status === 422 || error.statusCode === 422) {
        // Validation error - use the detailed message from API response
        errorMessage = error.message || 'Validation error. Please check your input and try again.';
      } else if (error.status === 404 || error.statusCode === 404) {
        // Not found
        errorMessage = error.message || 'Avatar endpoint not found. Please check the API configuration.';
      }

      // Determine auto-close duration based on error type
      const autoCloseDuration = (!error.status && !error.statusCode) || (error.status >= 500 || error.statusCode >= 500)
        ? 8000  // Longer for network/server errors
        : 5000; // Shorter for client errors

      toast.error(errorMessage, {
        toastId: (error.status === 401 || error.statusCode === 401) ? 'session-expired' : 'avatar-creation-error',
        autoClose: autoCloseDuration
      });
    } finally {
      if (!backgroundJobStarted) {
        setLoading(false);
      }
    }
  };


  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="create-avatar-page">
      <Navbar variant="transparent" />

      {loading && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(255,255,255,0.92)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: '32px',
        }}>
          <div style={{ maxWidth: '480px', width: '100%', textAlign: 'center' }}>
            <div style={{
              width: '100%', height: '6px', background: 'var(--border-subtle)',
              borderRadius: '3px', overflow: 'hidden', marginBottom: '28px',
            }}>
              <div style={{
                height: '100%',
                background: 'var(--soft-teal)',
                borderRadius: '3px',
                width: `${progressWidth}%`,
                transition: 'width 1.2s ease',
              }} />
            </div>
            <p style={{
              fontFamily: 'var(--font-serif)', fontSize: '22px',
              fontWeight: 400, color: 'var(--soft-black)', marginBottom: '12px',
            }}>
              {progressMessage || 'Getting started…'}
            </p>
            <p style={{ fontSize: '14px', color: 'var(--slate)' }}>
              This may take a few minutes depending on how many files you uploaded — please keep this page open.
            </p>
          </div>
        </div>
      )}

      {showFileLimitModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'rgba(255,255,255,0.95)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '24px',
        }}>
          <div style={{
            maxWidth: '520px', width: '100%',
            background: 'var(--warm-white)',
            borderRadius: '24px',
            padding: '40px',
            border: '1px solid var(--border-subtle)',
            boxShadow: 'var(--shadow-soft)',
            textAlign: 'center',
          }}>
            <h2 style={{
              fontFamily: 'var(--font-serif)', fontSize: '28px',
              fontWeight: 400, color: 'var(--soft-black)', marginBottom: '20px',
            }}>
              Start with your best memories
            </h2>
            <p style={{ fontSize: '16px', color: 'var(--charcoal)', lineHeight: 1.6, marginBottom: '16px' }}>
              You've selected <strong>{fileLimitFileCount} files</strong> — more than we need to get started. The most meaningful documents are often the best ones: letters, emails, journal entries, anything that really sounds like them.
            </p>
            <p style={{ fontSize: '16px', color: 'var(--charcoal)', lineHeight: 1.6, marginBottom: '32px' }}>
              Once their Avatar is created, you can always come back to add more.
            </p>
            <button
              onClick={() => {
                setShowFileLimitModal(false);
                const inp = document.getElementById('file-upload-input') || document.querySelector('input[type="file"]');
                if (inp) { inp.value = ''; inp.click(); }
              }}
              style={{
                background: 'var(--soft-teal)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '14px 32px',
                fontSize: '16px',
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'var(--font-sans)',
              }}
            >
              Choose My Files
            </button>
          </div>
        </div>
      )}

      <Container className="upload-container">
        <div className="upload-header">
          <h1>Create an Avatar</h1>
        </div>

        <div className="upload-form-wrapper">
          <Form onSubmit={handleSubmit(onSubmit)} className="upload-form">
            {/* Name Fields */}
            <div className="form-section">
              <label className="form-label-custom">Who is this Avatar of? <span className="required-asterisk">*</span></label>
              <Row>
                <Col md={6} className="mb-3 mb-md-0">
                  <Form.Label className="form-label-custom">First Name <span className="required-asterisk">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="First name"
                    className={`form-input ${errors.firstName ? 'is-invalid' : ''}`}
                    {...register('firstName', {
                      required: 'First name is required'
                    })}
                  />
                  {errors.firstName && (
                    <div className="error-message">{errors.firstName.message}</div>
                  )}
                </Col>
                <Col md={6}>
                  <Form.Label className="form-label-custom">Last Name <span className="required-asterisk">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Last name"
                    className={`form-input ${errors.lastName ? 'is-invalid' : ''}`}
                    {...register('lastName', {
                      required: 'Last name is required'
                    })}
                  />
                  {errors.lastName && (
                    <div className="error-message">{errors.lastName.message}</div>
                  )}
                </Col>
              </Row>
              {duplicateNameError && (
                <div className="error-message" style={{ marginTop: '4px' }}>{duplicateNameError}</div>
              )}
            </div>

            {/* Relationship Dropdown */}
            <div className="form-section">
              <label className="form-label-custom">You are their</label>
              <Form.Select
                className="form-input"
                value={selectedRelation || ''}
                onChange={(e) => setSelectedRelation(e.target.value || null)}
              >
                <option value="">Select relationship…</option>
                {RELATION_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </Form.Select>
            </div>

            {/* File Upload */}
            <div className="form-section">
              <label className="form-label-custom">Upload memories <span className="required-asterisk">*</span></label>
              <p style={{ fontSize: '13px', color: 'var(--slate)', marginBottom: '10px', lineHeight: 1.5 }}>
                For the best results, start with up to 50 of your most meaningful files — letters, emails, or anything that really sounds like them. You can always add more after their Avatar is created.
              </p>
              <div
                className={`dropzone ${isDragging ? 'dragging' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {/* File input for individual files */}
                <input
                  type="file"
                  id="file-input"
                  multiple
                  onChange={handleFileInput}
                  style={{ display: 'none' }}
                  accept=".txt,.doc,.docx,.pdf,.rtf,.odt,.html,.htm,.eml,.msg,.csv,.json,.md,.log,.zip,.rar,.7z,.xls,.xlsx,.xlsm,application/zip,application/x-zip-compressed,application/x-rar-compressed,application/x-7z-compressed"
                />
                {/* Folder input for folder selection */}
                <input
                  type="file"
                  id="folder-input"
                  webkitdirectory=""
                  directory=""
                  multiple
                  onChange={handleFileInput}
                  style={{ display: 'none' }}
                />
                <div
                  className="dropzone-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    document.getElementById('file-input').click();
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <div className="dropzone-text">Drag & Drop written content</div>
                <div className="dropzone-actions">
                  <Dropdown onClick={(e) => e.stopPropagation()}>
                    <Dropdown.Toggle as="button" type="button" className="dropzone-btn dropzone-btn-primary" id="upload-dropdown">
                      Upload
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="shadow-sm border-0 mt-2" style={{ borderRadius: '12px', minWidth: '160px' }}>
                      <Dropdown.Item
                        className="py-2 px-3 fw-medium d-flex align-items-center gap-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          document.getElementById('file-input').click();
                        }}
                      >
                        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Select Files
                      </Dropdown.Item>
                      <Dropdown.Divider className="my-1" />
                      <Dropdown.Item
                        className="py-2 px-3 fw-medium d-flex align-items-center gap-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          document.getElementById('folder-input').click();
                        }}
                      >
                        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                        </svg>
                        Select Folder
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
                <div className="file-types">
                  <span className="file-type-tag">Emails</span>
                  <span className="file-type-tag">Messages</span>
                  <span className="file-type-tag">Journals</span>
                  <span className="file-type-tag">Documents</span>
                </div>
              </div>

              {/* Upload summary only: number of files + total size (no per-file list) */}
              {uploadedFiles.length > 0 && (
                <div className="uploaded-files" ref={uploadedFilesRef}>
                  <div className="total-size-indicator" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px 24px' }}>
                    <span className="total-size-label">
                      {hasRestoredFilePlaceholdersOnly() ? '0 memories uploaded' : `${uploadedFiles.length} ${uploadedFiles.length === 1 ? 'memory' : 'memories'} uploaded`}
                    </span>
                  </div>
                  {hasRestoredFilePlaceholdersOnly() && (
                    <div style={{ marginTop: '8px', color: '#b45309', fontSize: '0.9rem' }}>
                      Files shown above are restored references. Please select files again before creating avatar.
                    </div>
                  )}
                </div>
              )}

            </div>

            <CustomButton
              type="submit"
              variant="gold"
              fullWidth
              loading={loading}
              className="form-submit"
              disabled={loading || createdAvatarId}
            >
              {loading ? 'Creating Avatar...' : 'Create Avatar'}
            </CustomButton>
          </Form>
        </div>
      </Container>



      {/* Payment Modal */}
      <CustomModal
        show={showPaymentModal}
        onHide={() => {
          if (!processingPayment) {
            setShowPaymentModal(false);
            setPendingAvatarData(null);
          }
        }}
        title="Complete Payment"
        subtitle="Please complete your payment to create the avatar"
      >
        {(() => {
          const selectedAvatarType = avatarPlans.find(avatarTypeItem => normalizeAvatarTypeName(avatarTypeItem.name) === normalizeAvatarTypeName(avatarType));
          const availablePlans = selectedAvatarType?.plans || [];
          const monthlyPlan = availablePlans.find(p => p.monthly_price_usd);
          const yearlyPlan = availablePlans.find(p => p.yearly_price_usd);
          const currentPlan = billingPeriod === 'yearly' ? yearlyPlan : monthlyPlan;
          const planPrice = currentPlan
            ? (billingPeriod === 'yearly'
              ? `$${currentPlan.yearly_price_usd}/year`
              : `$${currentPlan.monthly_price_usd}/month`)
            : '';

          return (
            <div className="payment-modal-content">
              <div className="payment-summary">
                <div className="payment-item">
                  <span className="payment-label">Plan:</span>
                  <span className="payment-value">
                    {selectedAvatarType?.name || 'Avatar Plan'}
                  </span>
                </div>
                <div className="payment-item">
                  <span className="payment-label">Billing Period:</span>
                  <span className="payment-value">{billingPeriod === 'yearly' ? 'Yearly' : 'Monthly'}</span>
                </div>
                <div className="payment-item payment-total">
                  <span className="payment-label">Total Amount:</span>
                  <span className="payment-value">{planPrice}</span>
                </div>
                {selectedPlan && (
                  <>
                    <div className="payment-item">
                      <span className="payment-label">Duration:</span>
                      <span className="payment-value">{selectedPlan.plan_days ?? selectedPlan.days ?? selectedPlan.free_trial_days ?? '30'} days</span>
                    </div>
                    <div className="payment-item">
                      <span className="payment-label">Token Limit:</span>
                      <span className="payment-value">{selectedPlan.token_limit ?? selectedPlan.tokens_limit ?? selectedPlan.max_tokens ?? '450'}</span>
                    </div>
                    <div className="payment-item">
                      <span className="payment-label">Storage:</span>
                      <span className="payment-value">{selectedPlan.max_storage_gb ?? selectedPlan.storage_limit ?? selectedPlan.storage_limit_gb ?? selectedPlan.storage ?? '5'}GB</span>
                    </div>
                    <div className="payment-item">
                      <span className="payment-label">Family Members:</span>
                      <span className="payment-value">{selectedPlan.max_family_members ?? selectedPlan.family_members ?? selectedPlan.family_member_limit ?? selectedPlan.members_limit ?? '1'}</span>
                    </div>
                  </>
                )}
              </div>

              <div className="payment-methods">
                <div className="payment-method-header">Select Payment Method</div>
                <button
                  type="button"
                  className="payment-method-btn paypal-btn"
                  onClick={processPayment}
                  disabled={processingPayment}
                >
                  {processingPayment ? (
                    <>
                      <div className="payment-spinner"></div>
                      Preparing checkout...
                    </>
                  ) : (
                    <>
                      <PayPalLogo />
                    </>
                  )}
                </button>
              </div>

              <div className="payment-note">
                <p>This is a demo payment. In production, this would redirect to PayPal for secure payment processing.</p>
              </div>
            </div>
          );
        })()}
      </CustomModal>

      {/* Gift Confirmation Modal (after signup from invite link) */}
      <CustomModal
        show={showSignupInviteModal && (() => {
          try {
            const raw = localStorage.getItem('signup_invite_params');
            if (!raw) return false;
            const parsed = JSON.parse(raw);
            if (!parsed?.invite_id) return false;
            const iWillPay = parsed.i_will_pay;
            return iWillPay != null || iWillPay == 1;
          } catch { return false; }
        })()}
        onHide={() => { }} // No-op as we want it to be mandatory
        title="Confirm Invitations"
        size="md"
        hideCloseButton={true}
        backdrop="static"
      >
        <div style={{ textAlign: 'center', padding: '10px' }}>
          <p style={{ fontSize: '18px', marginBottom: '32px', lineHeight: '1.6', color: '#111827', fontWeight: '500' }}>
            Confirm you would like to give <strong>{inviterName || 'a member'}</strong> a 1 year membership. Your account will be charged $149. Thank you
          </p>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CustomButton
              variant="primary"
              style={{ paddingLeft: '40px', paddingRight: '40px' }}
              onClick={() => {
                setShowSignupInviteModal(false);
                // Pre-select yearly plan for "Loved One" as this is the likely context for i_will_pay=0 gifts
                setAvatarType('for loved once');
                setBillingPeriod('yearly');
                if (avatarPlans && avatarPlans.length > 0) {
                  const lovedOne = avatarPlans.find(t => normalizeAvatarTypeName(t.name).includes('loved'));
                  if (lovedOne?.plans) {
                    const yearly = lovedOne.plans.find(p => p.yearly_price_usd === 149 || p.yearly_price_usd === 150 || Number(p.plan_days) === 365);
                    if (yearly) {
                      setSelectedPlanId(yearly.id);
                    }
                  }
                }
              }}
            >
              Confirm
            </CustomButton>
          </div>
        </div>
      </CustomModal>

      {/* Invite Email Modal - Shown after avatar creation if extracted_emails is returned */}
      <CustomModal
        show={showInviteModal}
        onHide={handleSkipInvites}
        className="invite-modal"
        title={`Who would you like to invite to talk with ${inviteAvatarName || 'them'}?`}
        size="lg"
        backdrop="static"
      >
        <div className="invite-modal-content">
          <div className="invite-ready-header">
            <div className="invite-ready-subtitle">
              Add the names and email addresses of family and friends you'd like to invite.
            </div>
          </div>

          <div className="invite-emails-list">
            {/* When no members: show only "Add someone" and actions. No "X person found / X selected". */}
            {inviteRows.length > 0 && (
              <>
                {/* Scrollable full list */}
                <div
                  className="invite-rows-full-list"
                  style={{ overflowY: 'auto', overflowX: 'visible' }}
                >
                  {inviteRows.map((row) => {
                    return (
                      <div
                        key={row.id}
                        data-row-id={row.id}
                        className={`invite-row ${row.selected ? 'selected' : ''} ${row.disabled ? 'invite-row-disabled' : ''}`}
                      >
                        <div className="invite-row-info">
                          <div className="invite-row-edit">
                            <input
                              className="invite-row-input invite-row-name-input"
                              placeholder="Name"
                              value={row.name || ''}
                              onChange={(e) => updateInviteRowField(row.id, 'name', e.target.value)}
                              readOnly={!row.isManual || row.disabled}
                            />
                            <div className="invite-row-email-wrapper">
                              <input
                                className="invite-row-input invite-row-email-input"
                                placeholder={row.name_only ? 'Enter their email address' : 'Email'}
                                value={row.email || ''}
                                onChange={(e) => updateInviteRowField(row.id, 'email', e.target.value)}
                                readOnly={(!row.isManual && !row.name_only) || row.disabled}
                              />
                              {row.selected && (
                                <div className="invite-row-action-badge">
                                  {row.disabled ? (
                                    <span className="invite-row-sent-badge" title="Invitation sent">Sent</span>
                                  ) : (row.action || 'send_invite') === 'send_invite' ? (
                                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18" title="Send Invite">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                  ) : isInviteGiftAction(row.action) ? (
                                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18" title="Gift">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                                    </svg>
                                  ) : null}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="invite-row-actions">
                          <div className="invite-action-select-wrapper">
                            <button
                              type="button"
                              className="invite-action-select-btn"
                              disabled={row.disabled}
                              onClick={(e) => {
                                if (row.disabled || isBetaInviter) return;
                                fetchAvatarPlan();
                                const newOpenId = openDropdownId === row.id ? null : row.id;
                                if (newOpenId === row.id) {
                                  // Decide upward + compute fixed anchor position before opening (prevents scroll jumps)
                                  const opensUp = getShouldOpenUpward(e.currentTarget);
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setDropdownOpensUpward(prev => ({ ...prev, [row.id]: opensUp }));
                                  setDropdownPosition(prev => ({
                                    ...prev,
                                    [row.id]: {
                                      left: rect.left,
                                      top: opensUp ? (rect.top - EST_DROPDOWN_HEIGHT - 4) : (rect.bottom + 4),
                                      width: rect.width
                                    }
                                  }));
                                }
                                setOpenDropdownId(newOpenId);
                                if (newOpenId === row.id) {
                                  checkDropdownPosition(e.currentTarget, row.id);
                                }
                              }}
                            >
                              <div className="invite-action-icon">
                                {getOptionIcon(row.action || 'send_invite')}
                              </div>
                              <span className="invite-action-text">
                                {row.disabled ? 'Sent' : (getDropdownOptions().find(opt => opt.value === (row.action || 'send_invite'))?.label || 'Send Invite')}
                              </span>
                              {!isBetaInviter && (
                                <div className="invite-action-arrow">
                                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width="12" height="12">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={openDropdownId === row.id ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                                  </svg>
                                </div>
                              )}
                            </button>
                            {openDropdownId === row.id && !row.disabled && !isBetaInviter && (
                              <div
                                className="invite-action-dropdown-menu dropdown-fixed"
                                data-row-id={row.id}
                                style={{
                                  position: 'fixed',
                                  left: dropdownPosition[row.id]?.left ?? 0,
                                  top: dropdownPosition[row.id]?.top ?? 0,
                                  width: dropdownPosition[row.id]?.width ?? undefined,
                                  zIndex: 2000
                                }}
                              >
                                {getDropdownOptions().map((option) => (
                                  <button
                                    key={option.value}
                                    type="button"
                                    className={`invite-action-dropdown-item ${(row.action || 'send_invite') === option.value ? 'active' : ''}`}
                                    onClick={() => {
                                      const newAction = option.value;
                                      if (process.env.NODE_ENV === 'development') {
                                        console.log(`Changing action for row ${row.id} (${row.email}):`, {
                                          oldAction: row.action,
                                          newAction,
                                          fullRow: row
                                        });
                                      }
                                      setInviteRowAction(row.id, newAction);
                                      setOpenDropdownId(null);
                                    }}
                                  >
                                    <div className="invite-action-dropdown-icon">
                                      {getOptionIcon(option.value)}
                                    </div>
                                    <span>{option.label}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>

                          <button
                            type="button"
                            className="invite-row-remove"
                            onClick={() => !row.disabled && removeInviteRow(row.id)}
                            aria-label="Remove"
                            title="Remove"
                            disabled={row.disabled}
                          >
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            <div className="invite-add-someone">
              <button type="button" className="invite-add-trigger" onClick={addBlankInviteRow}>
                <span className="invite-add-plus">+</span> Add someone
              </button>
            </div>
          </div>

          <div className="invite-modal-actions">
            <CustomButton
              type="button"
              variant="secondary"
              onClick={handleSkipInvites}
              disabled={sendingInvites}
            >
              Skip
            </CustomButton>
            <CustomButton
              type="button"
              variant="gold"
              onClick={inviteRows.some(r => !r.disabled) ? handleSendInviteEmails : handleSkipInvites}
              loading={sendingInvites}
              disabled={sendingInvites}
            >
              {sendingInvites ? 'Sending...' : (inviteRows.some(r => !r.disabled) ? 'Send Invitations' : 'Done')}
            </CustomButton>
          </div>
        </div>
      </CustomModal>


    </div>
  );
};

export default CreateAvatarPage;
